/**
 * The shell: search, a virtualised list, a reading pane.
 *
 * SEARCH-FIRST, NOT BROWSE-FIRST, and that is the inversion the access pattern
 * demands rather than a style choice. The owner's stated habit is that search is
 * how he reaches mail; folders are how an automated agent files it. So the
 * search box owns the top of the shell and every destination in the nav is a
 * QUERY (`in:INBOX`) rather than a folder the list knows how to open — which is
 * also what lets "unread in Archive" and "Inbox" be the same kind of thing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  api,
  HearthError,
  type MailboxState,
  type MessageRow,
  type SearchNote,
  type SearchCount,
  type Whoami,
} from './api.ts'
import { Compose, type ComposeDraft } from './Compose.tsx'
import { Rules } from './Rules.tsx'
import { Outbox } from './Outbox.tsx'
import { Inspector } from './Inspector.tsx'
import { Conversation, conversationKey } from './Thread.tsx'
import { countLabel, flagSet, fullDate, isFlagged, isUnread, listDate, many, recipientLine, senderName } from './format.ts'
import { readRowHeight, watchRowHeight } from './rowHeight.ts'
import {
  clearSelection,
  cursorRow as cursorRowOf,
  emptySelection,
  extendTo,
  moveCursor,
  confirmationFor,
  escalate,
  escalationPrompt,
  resolveTarget,
  selectAllLoaded,
  selectWhere,
  setCursor,
  toggleAtCursor,
  type SelectionState,
} from './model/selection.ts'
import {
  applyOverrides,
  DEFAULT_BINDINGS,
  KEY_OVERRIDES_PATH,
  overridesFromSettings,
  type Overrides,
} from './model/keymap.ts'
import { useKeyboard } from './useKeyboard.ts'
import { Help } from './Help.tsx'
import { Picker } from './Picker.tsx'
import { useOps } from './useOps.ts'
import { Toast } from './Toast.tsx'
import { Settings } from './Settings.tsx'
import { readAppearance, type PageSize } from './appearance.ts'
import { groupParam, readGrouping, readReadingPrefs, writeGrouping, type Grouping } from './settings.ts'
import { Screener } from './Screener.tsx'
import { Console } from './Console.tsx'
import { Admin } from './Admin.tsx'
import { ConfirmDialog, EscalationBanner } from './Confirm.tsx'
import { catchUpNeeded, SILENT_LABEL } from './model/optimistic.ts'
import { usePush } from './usePush.ts'
import { resolvePath, validateLeaf } from '@hearth/core/settings'
// COUNTING RECIPIENTS IS NOT NAMING ONE. `format.ts` owns turning a header into
// a display name and this does not second-guess it; the parser is imported for
// the one thing that file has no function for — how many addresses are in the
// list — and a display name can contain a comma, so splitting on one is wrong.
import { parseAddressList } from '@hearth/core/addresses'
import './settings.css'

/**
 * The three capabilities that reach the Server screen, any one of which opens it.
 *
 * THEY ARE THREE POWERS AND WERE TREATED AS ONE. The origin has told
 * `admin.accounts`, `admin.logs` and `admin.server` apart since the
 * verb-to-capability table landed — `account.*` is account administration, the
 * audit trail is a log, and everything else including `master.rotate` is the
 * server — while this rail asked only about `admin.server`. So a household that
 * granted somebody `admin.logs` gave them a capability that reached no screen at
 * all, which is the same "gates nothing" the table was written to end, one tier
 * further up. The panel picks the tabs; this decides whether there is a door.
 *
 * ALL THREE ARE `TAILNET_ONLY` at the origin and `/whoami` reports the set it
 * RESOLVED, so this needs no second copy of that rule: on the public path the
 * set simply does not contain them.
 */
const ADMIN_CAPABILITIES = ['admin.server', 'admin.accounts', 'admin.logs'] as const

/**
 * How this account reads mail.
 *
 * FOUR SETTINGS THE REGISTRY DECLARED AND NOTHING CONSULTED. `/reading/*` and
 * `/appearance/reading_pane` have been validated and storable since the
 * registry was written, and no line of client code read any of them — so a
 * household could set "never mark read" and be marked read anyway, if anything
 * had marked anything read at all, which it did not.
 */
export interface ReadingPrefs {
  /** `0` opens-is-read, `-1` never. See the registry for why it is not a boolean. */
  readonly markReadAfterMs: number
  readonly quotedText: 'fold' | 'show'
  readonly remoteImages: 'never' | 'contacts' | 'always'
  readonly readingPane: 'right' | 'below' | 'none'
}

/**
 * A setting's default, TAKEN FROM THE REGISTRY rather than retyped here.
 *
 * `2000`, `fold`, `contacts` and `right` are stated once, in
 * `packages/core/src/settings`, and this reads them back through the same
 * `validateLeaf` the origin uses on a write — so a value the registry declares
 * and a value this client will act on cannot disagree. Two places stating one
 * fact is this repo's most common bug.
 *
 * `whenAbsent` IS NOT A COPY OF THE DEFAULT. It is what to do if the key ever
 * stops being declared, and each one is deliberately the INERT answer: a
 * renamed key must fail towards touching nobody's mail, not towards the
 * behaviour somebody meant to keep.
 */
function registryDefault(path: string, whenAbsent: string | number): string | number {
  const declared = resolvePath(path)?.spec.default
  // `undefined` fails `validateLeaf` for every type here, so a missing key and
  // a malformed one take the same branch without a second test for it.
  return validateLeaf(path, declared) === null ? (declared as string | number) : whenAbsent
}

/**
 * The registry's answers, which is what the shell holds until somebody's own
 * are read back.
 *
 * The casts are safe by construction: `registryDefault` only returns a value
 * `validateLeaf` accepted for that path, and for an enum that means membership
 * of the spec's own `values` list. TypeScript cannot see through the registry
 * lookup to know it.
 */
export const DEFAULT_READING_PREFS: ReadingPrefs = {
  markReadAfterMs: registryDefault('/reading/mark_read_after_ms', -1) as number,
  quotedText: registryDefault('/reading/quoted_text', 'fold') as 'fold' | 'show',
  remoteImages: registryDefault('/reading/remote_images', 'never') as 'never' | 'contacts' | 'always',
  readingPane: registryDefault('/appearance/reading_pane', 'right') as 'right' | 'below' | 'none',
}

/** The server clamps `limit` to this, so a bigger page is several requests. */
/**
 * Tomorrow at 08:00, in the reader's own timezone.
 *
 * A MOMENT AND NOT A DURATION, which is what the origin's op takes and what
 * makes the wake survive a restart: the column holds an instant, so a server
 * that was down at 08:00 wakes the message when it comes back rather than
 * counting a fresh timer from zero.
 *
 * EIGHT IN THE MORNING rather than twenty-four hours from now, because "snooze"
 * means "not now, but at a time I will be reading mail" — and a message snoozed
 * at 23:50 that reappears at 23:50 tomorrow has been moved rather than
 * postponed. `setHours` on a local Date puts it in the reader's day rather than
 * in UTC's, which for this household is an hour or two of difference and for a
 * traveller is most of one.
 */
export function tomorrowMorning(now: Date = new Date()): number {
  const wake = new Date(now)
  wake.setDate(wake.getDate() + 1)
  wake.setHours(8, 0, 0, 0)
  return Math.floor(wake.getTime() / 1000)
}

/**
 * "I will answer this, but not now", as an IMAP keyword.
 *
 * A KEYWORD AND NOT A FOLDER, which is what lets `h` be pressed on a message
 * without moving it away from whatever the reader is looking at — and what lets
 * `g h` be an ordinary query like every other destination in this shell, rather
 * than a second kind of place.
 *
 * LOWERCASE, AND THE `$` PREFIX IS THE DESIGN'S. `compile-sql.ts` spells the
 * two reserved keywords it already knows `$muted` and `$pinned`, so this is the
 * third member of a set rather than a new convention. It is stated ONCE here
 * and the query below is built from it, because the key that writes the keyword
 * and the destination that lists it are the same fact twice — and a `g h` that
 * searched for a keyword `h` never sets is a destination that is permanently
 * empty and never wrong-looking.
 *
 * NOT `$ReplyLater` as `interaction.md` writes it: `message_keywords` stores
 * what the server echoes back, `label:` matches it exactly, and the lowercase
 * spelling is the one the two operators in the index already use.
 */
export const REPLY_LATER_KEYWORD = '$replylater'

/**
 * The keyword a muted conversation carries, and where a muted one goes.
 *
 * `$muted` IS THE SAME STRING THE GENERATED SIEVE WRITES and the same one
 * `is:muted` compiles against — three readers, one spelling, and it is spelled
 * here because this file is the one that applies it to the messages already on
 * screen. The rule the origin installs applies it to the ones that have not
 * arrived yet.
 *
 * BY SPECIAL-USE AND NEVER BY NAME, like everything else that names a folder:
 * this household's archive is called `Archiv`.
 */
export const MUTED_KEYWORD = '$muted'
const ARCHIVE_SPECIAL_USE = '\\Archive'
export const REPLY_LATER_QUERY = `label:${REPLY_LATER_KEYWORD}`

/**
 * Everything, in every mailbox.
 *
 * AN EMPTY QUERY IS THE "ALL MAIL" QUERY — `parseQuery('')` is `{op: 'true'}`
 * and the origin compiles it to `1 = 1`, which is the same thing `/ops/query`
 * already means by `q: ''`. So `g a` needs no new operator and no folder that
 * this household's server does not have: it is the search box, emptied.
 */
const ALL_MAIL_QUERY = ''

/**
 * The mailbox carrying an IMAP SPECIAL-USE flag, or null.
 *
 * SPECIAL-USE, NEVER THE NAME — `useOps.ts` and `shell-ia.md` state the rule and
 * this household is the proof of it: its Trash is called `Papierkorb`. Stated
 * once for this file because three things now need the same lookup (the
 * query-wide move, `g t` and `g d`), and `useOps` keeps its own private copy
 * only because a hook cannot export a helper the shell would then import back.
 */
function specialBox(mailboxes: readonly MailboxState[], specialUse: string): MailboxState | null {
  return mailboxes.find((box) => (box.specialUse ?? '').toLowerCase() === specialUse.toLowerCase()) ?? null
}

/**
 * The query a mailbox stands for.
 *
 * ONE SPELLING FOR THE NAV AND FOR THE `g` CHORDS, because they mean the same
 * thing: clicking "Archive" and pressing `g t` both put a destination's query in
 * the search box, and two spellings of it would differ first over the quoting.
 * A name containing whitespace MUST be quoted — the parser reads an unquoted
 * `in:Sent items` as `in:Sent` and a bare word `items`, which is a different
 * search that also happens to work.
 */
export function mailboxQuery(name: string): string {
  return `in:${/\s/.test(name) ? JSON.stringify(name) : name}`
}

const SERVER_MAX_LIMIT = 200

/**
 * The frozen half of a walk: everything the cursor's fingerprint is computed
 * over, which is therefore everything that must be sent on EVERY request of it.
 *
 * IT IS AN ARGUMENT AND NOT A CONSTANT because there are two walks now. The
 * list's is grouped however the reader asked and counted once; a conversation's
 * is `group=message` — asking a `thread:` query for `group=thread` collapses the
 * conversation straight back into the one row that hid it — ordered explicitly,
 * and counted not at all, because `thread_count` already said how many there are.
 */
export interface WalkPlan {
  readonly group: 'message' | 'thread'
  /** Omitted means the server's own default, which is newest first. */
  readonly order?: 'asc' | 'desc'
  /** `'none'` for a walk whose length is already known. */
  readonly count?: '1' | 'none'
}

/**
 * Fill one page, however many requests that takes.
 *
 * A SHORT PAGE IS ORDINARY AND IS NOT THE END. The scan budget cuts one
 * whenever a query is expensive, so `nextCursor` is the only end-of-walk signal
 * there is — a loop that stopped on a short response would silently show fewer
 * messages than were asked for and call it done.
 *
 * The iteration bound is not a page-size limit; it is a guard against a server
 * that keeps handing back a cursor and no rows. Without it that is an infinite
 * loop in a tab.
 */
export async function fillPage(
  query: string,
  want: number,
  from: string | null,
  plan: WalkPlan,
): Promise<{
  rows: MessageRow[]
  cursor: string | null
  count: SearchCount | null
  seq: number
  notes: SearchNote[]
}> {
  const rows: MessageRow[] = []
  let cursor = from
  let count: SearchCount | null = null
  // ONE PAGE IS SEVERAL REQUESTS, so a note can arrive on any of them — and the
  // same note arrives on every request that hits the same limit. Deduped on
  // code AND term, because one query can floor two different body terms and
  // both are worth saying.
  const notes = new Map<string, SearchNote>()
  // THE LIST ITSELF IS WHAT ADVANCES THE WATERMARK. Every search response
  // carries the change sequence it was answered at, so there is no second
  // bookkeeping path to keep in step with this one — and a hint that does not
  // beat it is a hint about changes this page already contains.
  let seq = 0

  for (let request = 0; rows.length < want && request < 32; request++) {
    const page = await api.search({
      q: query,
      ...(cursor ? { cursor } : {}),
      limit: Math.min(want - rows.length, SERVER_MAX_LIMIT),
      // Counted once, on the first request of the first page. Pages 2..n of a
      // walk are not counted at all, which is what `count=1` already means —
      // and a walk that asked for no count at all gets none on page 1 either.
      // `none` IS SENT, NOT OMITTED, and omitting it was a no-op dressed as an
      // optimisation. The origin defaults the parameter to `1`, so a walk that
      // "asked for no count" ran exactly the count query it claimed to avoid —
      // and the conversation walk, whose whole argument is that `thread_count`
      // already said how big the thread is, paid for a second query per
      // conversation opened. The test asserted the parameter's ABSENCE, which
      // pinned the bug rather than the intent.
      ...(from === null && request === 0
        ? { count: plan.count === 'none' ? ('none' as const) : ('1' as const) }
        : {}),
      // SENT ON EVERY REQUEST OF THE WALK, cursor or not. Both of these are
      // hashed into the cursor's fingerprint, so omitting either on pages 2..n
      // would present a cursor the server cannot match — `cursor_mismatch`, and
      // correctly so.
      ...(plan.order ? { order: plan.order } : {}),
      group: plan.group,
    })
    rows.push(...page.messages)
    for (const note of page.notes ?? []) notes.set(`${note.code} ${note.term}`, note)
    cursor = page.nextCursor
    if (count === null) count = page.count
    if (page.seq > seq) seq = page.seq
    if (!cursor) break
  }
  return { rows, cursor, count, seq, notes: [...notes.values()] }
}

interface Walk {
  readonly query: string
  readonly rows: readonly MessageRow[]
  readonly cursor: string | null
  readonly count: SearchCount | null
  readonly loading: boolean
  readonly error: string | null
  /** What the origin could not do exactly. See `SearchNote`. */
  readonly notes: readonly SearchNote[]
}

const EMPTY: Walk = { query: '', rows: [], cursor: null, count: null, loading: true, error: null, notes: [] }

export interface AppProps {
  /** A permalink ref to open on arrival. See `router.tsx`. */
  readonly openRef?: string
}

export function App({ openRef }: AppProps = {}): React.JSX.Element {
  const [query, setQuery] = useState('in:INBOX')
  const [submitted, setSubmitted] = useState('in:INBOX')
  const [walk, setWalk] = useState<Walk>(EMPTY)
  // THE CURSOR IS A ROW ID, NEVER AN INDEX — `selection.ts` records why: the
  // virtualiser recycles DOM nodes, and the change feed shifts every index
  // below an insertion. The model has held this since before the client did.
  const [selection, setSelection] = useState<SelectionState>(emptySelection)
  const [mailboxes, setMailboxes] = useState<readonly MailboxState[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  /**
   * This person's remapping, and the chords in it that name nothing.
   *
   * THE PIPELINE EXISTED END TO END AND HAD NO CALLER. `applyOverrides` and
   * `findConflicts` were written, tested and imported by nobody, and
   * `useKeyboard` has taken an `overrides` option since it was written — so the
   * one thing missing between a stored remapping and a remapped keyboard was
   * this fetch. It starts empty, which is the default map, and a slow or failed
   * read leaves it that way: a keyboard that waits for a preference before it
   * answers `j` is worse than one that ignores the preference.
   */
  const [overrides, setOverrides] = useState<Overrides>({ byId: {}, ignored: [] })
  const [draft, setDraft] = useState<ComposeDraft | null>(null)
  const [rulesOpen, setRulesOpen] = useState(false)
  const [inspecting, setInspecting] = useState<MessageRow | null>(null)
  /**
   * A queued message, and how long is left to take it back.
   *
   * THE HOLD IS THE ORIGIN'S, and this only renders it: `sendAfter` came back
   * from `/send` and is the moment the message becomes eligible, so a client
   * whose clock is wrong shows a wrong countdown and still cannot undo a
   * message that has gone — the origin refuses that with `too_late`.
   */
  const [queued, setQueued] = useState<{ id: number; sendAfter: number; undoSeconds: number } | null>(null)
  // Read once and kept in state so a change in Settings re-runs the walk: a new
  // page size is a different question to ask the server, not a different way to
  // display the answer already in hand.
  const [pageSize, setPageSize] = useState<PageSize>(() => readAppearance().pageSize)
  /**
   * Threads or messages — the one list decision `decisions.md` leaves to the
   * reader ("some like it this way, some the other").
   *
   * IT STARTS AT THE REGISTRY'S DEFAULT AND IS CORRECTED FROM THE SERVER, rather
   * than waiting for the read: the first paint is a real list either way, and a
   * shell that blocked on a preference request would show nothing at all when
   * that request is slow. The correction re-runs the walk, which is right — it
   * is a different question to ask the server, not a different way to draw the
   * answer in hand.
   */
  const [grouping, setGrouping] = useState<Grouping>('messages')
  /**
   * How this account wants mail read to it.
   *
   * THE REGISTRY'S ANSWERS UNTIL THIS PERSON'S ARE READ BACK, one request, in
   * the mount effect below. The read lives in `settings.ts` beside
   * `readGrouping` because that module is the only reader of the account
   * document — one `api.settings()` serving all four leaves rather than four —
   * and it takes these defaults as its fallback so `2000`, `fold`, `contacts`
   * and `right` stay stated once, in the registry.
   *
   * `mark_read_after_ms` IS THE ONE THAT WRITES TO THE MAIL SERVER, which is
   * why the read is not optional polish: while this was a frozen constant, an
   * account that had chosen `-1` — never mark anything read — got `\Seen`
   * written to its real IMAP mailbox two seconds after a message rested in the
   * pane anyway, on every device, with nothing in the client able to stop it.
   */
  const [readingPrefs, setReadingPrefs] = useState<ReadingPrefs>(DEFAULT_READING_PREFS)
  /**
   * IS THE READER READING, which is not the same fact as "there is a cursor
   * row" — and the shell used one for the other.
   *
   * `data-reading` was `cursorRow ? 'true' : 'false'`, and a cursor exists from
   * the moment the first page lands (`startWalk` pins it to row 0). Under
   * `max-width: 60rem` the stylesheet hides the LIST when `data-reading` is
   * true, so on a phone the client painted straight into a reading pane for a
   * message nobody chose, with the list gone and no way back to it. Two
   * predicates about one thing, and the one place it is visible is the one
   * viewport with no test coverage.
   *
   * Set only by things that MEAN "open this": Enter, a click on a row, and a
   * permalink arriving. Deliberately NOT by `setCursor` — `j` and `k` move the
   * cursor and open nothing — and deliberately not derived from `cursorRow`,
   * which is how the bug got here.
   */
  const [reading, setReading] = useState(false)
  /**
   * IS FOCUS IN THE MESSAGE, which is the only honest answer to "which keymap
   * context is this".
   *
   * DERIVED FROM FOCUS AND NOT FROM `cursorRow`. The first attempt at a `thread`
   * context keyed it on a cursor row existing — and a cursor exists from the
   * first paint, so `x`, `*` and range selection (all `list` bindings) were dead
   * from the first search. Focus is different in exactly the way that matters:
   * it moves only when the reader does something that means "I am in the
   * message now", and it moves back out again.
   */
  const [inMessage, setInMessage] = useState(false)
  /**
   * A counter the conversation watches, bumped ONLY by Enter.
   *
   * Enter is the keyboard's way into the message, and something has to carry
   * focus across the pane boundary for `n`, `p` and `a` to be reachable without
   * a mouse. A click deliberately does not bump it: stealing focus on every
   * opened row would put the shell in the `thread` context for anybody using a
   * pointer, which is the same regression the comment above is about.
   */
  const [focusSignal, setFocusSignal] = useState(0)
  /**
   * The message inside the conversation the reader is on, if they are on one.
   *
   * WHAT `r`, `a` AND `f` ACT ON when focus is in the pane. See
   * `ConversationProps.onFocusedMessage`: the cursor row of a conversation list
   * is the thread's NEWEST message, so replying to "the cursor row" from inside
   * a four-message thread answers the wrong person. Everything else — archive,
   * star, the permalink, the Inspector — still acts on the row, because those
   * are about the conversation rather than about one message in it.
   */
  const [inThread, setInThread] = useState<MessageRow | null>(null)
  const readPane = useRef<HTMLDivElement>(null)
  const [screenerOpen, setScreenerOpen] = useState(false)
  const [outboxOpen, setOutboxOpen] = useState(false)
  /**
   * How many messages could not be sent, for the badge.
   *
   * READ ON MOUNT AND AFTER A SEND, not polled. A failed send is rare and
   * permanent — nothing retries it — so the number does not move on its own,
   * and a poll would be a request every few seconds for a count that is almost
   * always zero. What it must never be is invisible: the only signal a sender
   * got was the "will send in Ns" toast, and a countdown reaching zero reads as
   * "sent".
   */
  const [failedSends, setFailedSends] = useState(0)
  const [consoleOpen, setConsoleOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  /**
   * How many senders are waiting, and whether the Screener has been seeded.
   *
   * KEPT SO THE INBOX CAN EXPLAIN ITSELF. `review.md`'s remedy for the blocker
   * is not only the seed — it is that "an empty Inbox is never unexplained", so
   * the count has to be somewhere the reader is already looking rather than
   * behind a destination they have to think to visit.
   */
  const [waiting, setWaiting] = useState<{ senders: number; ready: boolean } | null>(null)

  const refreshScreener = useCallback(() => {
    api
      .screener()
      .then((page) => setWaiting({ senders: page.senders.length, ready: page.ready }))
      // SILENT. The count is an explanation, not a feature; a household whose
      // Screener route is unreachable should see the shell it always sees.
      .catch(() => setWaiting(null))
  }, [])
  /** A destructive command waiting on the dialog. */
  const [pending, setPending] = useState<{ action: string; special: string } | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // THE WALK IS KEYED BY THE SUBMITTED QUERY, and every page of it must belong
  // to that key. Without the generation check, a slow page 1 for the previous
  // query lands after a fast page 1 for the new one and silently replaces it.
  const generation = useRef(0)

  /**
   * The highest change sequence this client has seen.
   *
   * Every list response carries one, so the walk itself is what advances it —
   * there is no second bookkeeping path to keep in step.
   */
  const localSeq = useRef(0)
  /**
   * A hint that arrived while the reader had a selection open.
   *
   * IT USED TO BE DROPPED. The hook ran `startWalk` unless a selection was
   * open, and did nothing at all when one was — so a hint that landed mid-triage
   * was gone for good, and the list stayed stale until the reader happened to
   * search again. Replacing rows under a half-built selection is still the
   * wrong thing to do; forgetting that it needs doing is a different mistake.
   */
  const deferred = useRef(false)
  /**
   * The query the current walk was started for, and where the cursor is.
   *
   * BOTH ARE REFS BECAUSE `startWalk` MUST NOT DEPEND ON THEM. It is the
   * dependency of the effect that runs it, so a callback that changed whenever
   * the cursor moved would re-run the whole walk on every `j` — which is both a
   * request storm and, since the walk resets the cursor, a loop.
   */
  const walked = useRef<string | null>(null)
  const cursorId = useRef<string | null>(null)

  const startWalk = useCallback(
    (q: string) => {
      const mine = ++generation.current
      /**
       * THE SAME QUESTION ASKED AGAIN IS NOT A NEW QUESTION.
       *
       * Every hint and every socket reconnect re-runs the CURRENT query, and
       * this used to pin the cursor to row 0 of whatever came back — so mail
       * arriving while somebody was reading swapped the message out from under
       * them mid-sentence, along with their scroll position inside it and their
       * decision to load its images. A flaky connection did it repeatedly.
       *
       * The existing deferral protects a half-built CHECKBOX selection; a cursor
       * is not a selection (the shell says so itself where `useKeyboard` is
       * called) and had nothing protecting it. Held by ID rather than by index,
       * for the reason the whole model is: the rows that came back are a
       * different array with different positions.
       */
      const sameQuestion = walked.current === q
      const held = sameQuestion ? cursorId.current : null
      walked.current = q
      setWalk({ ...EMPTY, query: q })
      setSelection(emptySelection)
      // A NEW LIST IS SOMETHING TO LOOK AT. Under the breakpoint the reading
      // pane covers the list, and a search whose results you cannot see is the
      // same trap by another route.
      if (!sameQuestion) setReading(false)
      fillPage(q, pageSize, null, { group: groupParam(grouping) })
        .then((page) => {
          if (generation.current !== mine) return
          if (page.seq > localSeq.current) localSeq.current = page.seq
          setWalk({
            query: q,
            rows: page.rows,
            cursor: page.cursor,
            count: page.count,
            loading: false,
            error: null,
            notes: page.notes,
          })
          // NOT FOUND FALLS BACK TO THE TOP, deliberately: the message really
          // has left this query — filed, deleted, or pushed off page one — and
          // there is no row to sit on. `findIndex` returning -1 is that case and
          // `setCursor` clamps it, so the answer is stated once.
          const at = held === null ? 0 : page.rows.findIndex((row) => row.id === held)
          setSelection(setCursor(emptySelection, at, page.rows))
        })
        .catch((error: unknown) => {
          if (generation.current !== mine) return
          setWalk({ ...EMPTY, query: q, loading: false, error: describeError(error) })
        })
    },
    [pageSize, grouping],
  )

  /**
   * ONE MORE PAGE, AND ONLY WHEN ASKED.
   *
   * There is no scroll trigger any more. Loading on scroll is a different
   * feature from a page size, and having both meant "100" did not mean 100 —
   * the list kept fetching as you moved down it. A button says what it will do
   * and does exactly that.
   */
  const loadMore = useCallback(() => {
    const mine = generation.current
    setWalk((current) => {
      if (!current.cursor || current.loading) return current
      const cursor = current.cursor
      fillPage(current.query, pageSize, cursor, { group: groupParam(grouping) })
        .then((page) => {
          if (generation.current !== mine) return
          setWalk((live) =>
            // Re-checked on arrival: two overlapping clicks would otherwise
            // append the same page twice.
            live.cursor !== cursor
              ? live
              : {
                  ...live,
                  rows: [...live.rows, ...page.rows],
                  cursor: page.cursor,
                  loading: false,
                  // MERGED, NOT REPLACED, and not dropped either. A note about
                  // the whole query (a floored body term) is still true on page
                  // two, and a limit that fires only deeper in the walk is new
                  // information. Same key as `fillPage` uses, so a note that
                  // recurs on every request is stated once.
                  notes: [
                    ...live.notes,
                    ...page.notes.filter(
                      (note) => !live.notes.some((had) => had.code === note.code && had.term === note.term),
                    ),
                  ],
                },
          )
        })
        .catch((error: unknown) => {
          if (generation.current !== mine) return
          // A STALE CURSOR IS THE ONE RECOVERABLE CODE, and the remedy is
          // stated in the envelope rather than inferred: start the walk again
          // from page 1. Anything else is left for the reader to see.
          if (error instanceof HearthError && error.recoverable) startWalk(current.query)
          else setWalk((live) => ({ ...live, loading: false, error: describeError(error) }))
        })
      return { ...current, loading: true }
    })
  }, [startWalk, pageSize, grouping])

  useEffect(() => startWalk(submitted), [submitted, startWalk])

  useEffect(() => refreshScreener(), [refreshScreener])

  // ONCE, ON MOUNT. `startWalk` depends on `grouping`, so setting it here re-runs
  // the walk through the effect above rather than needing a second fetch path.
  useEffect(() => {
    let live = true
    readGrouping().then((stored) => {
      if (live) setGrouping(stored)
    })
    return () => {
      live = false
    }
  }, [])

  // AND THE READING PREFERENCES, in one more request. Separate from the walk on
  // purpose: none of these four changes which messages are listed, so a slow
  // settings read must not hold the mail back. Until it lands the registry's
  // defaults apply, which is what the shell had before — the difference is that
  // now they stop applying.
  useEffect(() => {
    let live = true
    readReadingPrefs(DEFAULT_READING_PREFS).then((stored) => {
      if (live) setReadingPrefs(stored)
    })
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    api
      .mailboxes()
      .then((r) => setMailboxes(r.mailboxes))
      .catch(() => setMailboxes([]))
  }, [])

  /**
   * AND THE KEYBOARD REMAPPING, in one more request.
   *
   * IT BELONGS IN `settings.ts` BESIDE THE OTHER TWO READS, and it is here
   * because that file is not mine to edit in this change: `readGrouping` and
   * `readReadingPrefs` already fetch the same document twice on mount, and the
   * right shape is one `api.settings()` serving all three. Whoever next opens
   * that file should move this into it as `readKeyOverrides()` and delete these
   * lines — the projection and the reconciliation are already in `keymap.ts`,
   * so what moves is the request and nothing else.
   *
   * THE PATH IS CHECKED AGAINST THE REGISTRY rather than trusted. A leaf renamed
   * in `packages/core/src/settings` would otherwise leave this reading
   * `undefined` for ever, which looks exactly like "nobody has remapped
   * anything" — the silent failure the registry exists to prevent.
   */
  useEffect(() => {
    let live = true
    if (resolvePath(KEY_OVERRIDES_PATH) === null) {
      setNotice(`This build looks for ${KEY_OVERRIDES_PATH}, which its settings registry does not declare`)
      return
    }
    api
      .settings()
      .then(({ doc }) => {
        // The document is nested the way `project()` builds it, so the leaf path
        // is walked rather than indexed — the same walk `settings.ts` does for
        // its own leaves, and the reason this wants to live there.
        let node: unknown = doc
        for (const segment of KEY_OVERRIDES_PATH.split('/').filter(Boolean)) {
          node = typeof node === 'object' && node !== null ? (node as Record<string, unknown>)[segment] : undefined
        }
        // VALIDATED BY THE REGISTRY, not by a shape test written here: it is the
        // same check the origin makes on the way in, so a document this client
        // would act on and a document the origin would store cannot differ.
        if (live && validateLeaf(KEY_OVERRIDES_PATH, node) === null) setOverrides(overridesFromSettings(node))
      })
      // SILENT, like every other settings read: the default map is a correct
      // keyboard, and mail that will not appear because a preference could not
      // be fetched is a far worse failure than a preference that did not apply.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  /**
   * The map the resolver is actually holding.
   *
   * `applyOverrides` IS CALLED TWICE ON PURPOSE — here for the help sheet, and
   * inside `KeyResolver` for the keystrokes — because it is a pure function of
   * the same two inputs. That is derivation, not duplication: what must never
   * happen is the sheet building its own idea of the map, which is how a cheat
   * sheet comes to print a key that stopped working.
   */
  const bindings = useMemo(() => applyOverrides(DEFAULT_BINDINGS, overrides.byId), [overrides.byId])

  // WHAT COULD NOT GO. Re-asked whenever something is queued, because that is
  // the only moment the number can grow without the tray being open, and when
  // the tray closes, because a cancelled message leaves the list.
  const refreshOutbox = useCallback(() => {
    api
      .outbox()
      .then((r) => setFailedSends(r.messages.filter((m) => m.state === 'failed').length))
      .catch(() => undefined)
  }, [])
  useEffect(() => refreshOutbox(), [refreshOutbox, queued])

  /**
   * Who this is, and what they may do.
   *
   * NOTHING IN THE CLIENT WAS CAPABILITY-GATED. `useKeyboard` has always taken
   * a capability set and `keymap.ts` has always marked which bindings need one
   * — and no caller ever passed it, so every binding behaved as though the
   * reader held every capability. The visible symptom was the opposite of a
   * security hole and still a bug: a child pressing a key their role does not
   * hold got the generic "no permission" notice built for a capability the
   * client KNOWS is missing, from a client that knew nothing, and the owner got
   * it too.
   *
   * A FAILED FETCH LEAVES THE SET UNDEFINED, not empty. Empty means "holds
   * nothing", which would make every gated key inert on a network blip;
   * undefined means "not known yet", which `useKeyboard` already treats as
   * ungated. The origin re-checks every capability on every mutating route
   * regardless, so this set is about telling the truth in the UI, never about
   * enforcement.
   */
  const [whoami, setWhoami] = useState<Whoami | null>(null)

  useEffect(() => {
    api
      .whoami()
      .then(setWhoami)
      .catch(() => setWhoami(null))
  }, [])

  const capabilities = useMemo(
    () => (whoami ? new Set(whoami.capabilities) : undefined),
    [whoami],
  )

  /**
   * May this reader do the thing behind this button?
   *
   * CAPABILITY GATING IS NOT INHERITED FROM THE KEYMAP. It lives entirely in
   * `KeyResolver.gate` and reaches the shell as a `blocked:` id, so a button
   * bypasses it completely — every one of them has to ask.
   *
   * `undefined` MEANS "NOT KNOWN YET" AND GATES NOTHING, which is the rule
   * `keymap.ts` states about the same set and the reason it is stated once here
   * rather than at each button: a `/whoami` that failed on a flaky network is
   * not a principal who holds nothing, and hiding every control on a network
   * blip is the failure that rule exists to prevent.
   */
  const may = useCallback(
    (capability: string): boolean => capabilities === undefined || capabilities.has(capability),
    [capabilities],
  )

  /**
   * A permalink, resolved.
   *
   * The message is fetched and shown DIRECTLY rather than by searching for it:
   * a permalink names one message, and a search that happened to return it
   * would depend on it still matching some query. The list keeps whatever it
   * was showing, so the reader lands on the message with somewhere to go next.
   *
   * "Unknown" and "not yours" are the same 404 by design — the origin will not
   * say which — so the copy here must not guess either.
   */
  const [permalinkRow, setPermalinkRow] = useState<MessageRow | null>(null)
  /**
   * The question `l` and `v` opened, and the rows it is about.
   *
   * THE ROWS ARE HELD HERE AND NOT READ AGAIN WHEN IT ANSWERS. Between pressing
   * the key and choosing from the list the cursor can move, the selection can
   * clear and a live update can repaint the page — so a picker that asked
   * "which rows" at the END would label whatever happened to be under the cursor
   * a second later. What a person meant is what was under the key when they
   * pressed it.
   */
  const [picking, setPicking] = useState<{ kind: 'label' | 'move'; rows: readonly MessageRow[] } | null>(null)
  /**
   * Every label this account has used, read when a picker needs one.
   *
   * NOT ON MOUNT. It is one query against `message_keywords` and nothing else in
   * the shell wants it, so paying for it on every load — including the loads
   * where nobody ever presses `l` — would be a request per session for a screen
   * most sessions never open. Re-read on each open, because a label applied on
   * another device is exactly the one somebody is about to reach for.
   */
  const [labels, setLabels] = useState<readonly { label: string; messages: number }[]>([])
  useEffect(() => {
    if (!openRef) return
    let live = true
    api
      .resolveRef(openRef)
      .then((message) => {
        if (!live) return
        setPermalinkRow(message)
        // ARRIVING BY LINK IS OPENING THE MESSAGE. On a phone the list would
        // otherwise cover the thing the link pointed at.
        setReading(true)
      })
      .catch(() => {
        if (live) setNotice('That link does not point at a message in your mail')
      })
    return () => {
      live = false
    }
  }, [openRef])

  /**
   * NEW MAIL ARRIVES AS A SEQUENCE NUMBER AND NOTHING ELSE.
   *
   * The hint carries no content — `data.md` is explicit that none crosses the
   * edge — so the only thing to do with it is ask again. Re-running the current
   * query is the honest response and it is cheap: a page is under 6 ms server
   * side. It deliberately does NOT re-run while the reader has a selection,
   * because replacing the rows under a half-built selection loses their work
   * for something that can wait for the next hint.
   */
  const push = usePush({
    onHint: (hint) => {
      // A DUPLICATE HINT IS NOT A REASON TO RE-WALK. The hook delivers every
      // frame in arrival order and deliberately filters nothing — its own
      // comment says `catchUpNeeded` is where the comparison belongs, and until
      // now nothing made it. A reconnect that replays the last hint would
      // otherwise re-read the whole list for no change.
      if (!catchUpNeeded(localSeq.current, hint)) return
      if (selection.selected.size > 0) {
        deferred.current = true
        return
      }
      startWalk(submitted)
    },
  })

  // THE DEFERRED HINT, when the selection clears. This is the other half of not
  // dropping it: the reader finishes triaging, the list catches up, and the
  // mail that arrived meanwhile appears without them having to do anything.
  useEffect(() => {
    if (selection.selected.size > 0 || !deferred.current) return
    deferred.current = false
    startWalk(submitted)
  }, [selection.selected.size, submitted, startWalk])

  /**
   * A RECONNECT CATCHES UP UNCONDITIONALLY.
   *
   * Hints sent while the socket was down were sent to nobody — the Durable
   * Object fans out to the sockets it has — so coming back knowing only the seq
   * of the last hint received BEFORE the outage is knowing nothing about the
   * outage. The list is re-read once on every transition into `open` after a
   * failure, which is the cheapest correct answer and the same one a cold boot
   * uses.
   */
  const wasOpen = useRef(false)
  useEffect(() => {
    const open = push.state === 'open'
    if (open && !wasOpen.current && localSeq.current > 0) startWalk(submitted)
    wasOpen.current = open
  }, [push.state, submitted, startWalk])

  // THE TOAST GOES WHEN THE HOLD DOES. Leaving an Undo button on screen past
  // the moment it stops working is offering something that will be refused.
  useEffect(() => {
    if (!queued) return
    const remaining = (queued.sendAfter - Math.floor(Date.now() / 1000)) * 1000
    const timer = setTimeout(() => setQueued(null), Math.max(0, remaining))
    return () => clearTimeout(timer)
  }, [queued])

  const listCursor = useMemo(
    () => (cursorRowOf(selection, walk.rows) as MessageRow | undefined) ?? null,
    [selection, walk.rows],
  )
  // Mirrored so `startWalk` can read it without depending on it. See `walked`.
  cursorId.current = selection.cursorId
  // The permalink wins until the reader moves the cursor themselves, which is
  // what makes arriving by link feel like arriving at the message.
  const cursorRow = permalinkRow ?? listCursor

  const patchRows = useCallback(
    (patch: (rows: readonly MessageRow[]) => readonly MessageRow[]) =>
      setWalk((live) => ({ ...live, rows: patch(live.rows) })),
    [],
  )

  /**
   * The mailboxes whose rows are about who you WROTE to.
   *
   * SPECIAL-USE, NEVER THE NAME — the same rule `Destinations` and `useOps`
   * already follow, and for the same reason: this household's server is a stock
   * docker-mailserver whose owner can rename anything, and "Sent" is English.
   *
   * Drafts is here too. A draft has no meaningful sender either — it is the
   * account, on every row.
   */
  const outgoing = useMemo(
    () =>
      new Set(
        mailboxes
          .filter((box) => ['\\sent', '\\drafts'].includes((box.specialUse ?? '').toLowerCase()))
          .map((box) => box.name),
      ),
    [mailboxes],
  )

  const triage = useOps({ mailboxes, patchRows, onError: setNotice })

  /**
   * The message on screen has been read. Say so to the server, and to nobody
   * else.
   *
   * SILENT, AND THAT IS THE WHOLE DESIGN OF IT. Routed through the ordinary
   * `flag` label this would pop "Marked 1 read" every time the cursor rested on
   * a message — and, worse, `toast()` hands out the NEWEST batch for ten
   * seconds, so it would quietly replace the Undo for an archive the reader did
   * a moment ago and may still want back. `SILENT_LABEL` keeps everything else:
   * the optimistic patch, the rollback if the mail server refuses, and the
   * per-flag delta that lets two devices merge.
   *
   * NO UNDO IS OFFERED FOR IT, deliberately — nobody pressed anything, so there
   * is nothing to take back. `Shift+U` is the way to make it unread again, and
   * it is in the keymap for exactly this.
   */
  const markRead = useCallback(
    (row: MessageRow) => void triage.flag([row], ['\\Seen'], [], SILENT_LABEL),
    [triage],
  )

  /**
   * THE TARGET RULE, from `interaction.md`: act on the selection if there is
   * one, otherwise on the cursor row. It is one line and it is the difference
   * between `e` being predictable and `e` being a guess.
   */
  const targets = useMemo((): MessageRow[] => {
    if (selection.selected.size > 0) return walk.rows.filter((row) => selection.selected.has(row.id))
    return cursorRow ? [cursorRow] : []
  }, [selection, walk.rows, cursorRow])

  /**
   * What the count means for a bulk action.
   *
   * `count` is `{value, exact}` and `exact: false` means the true number is
   * GREATER — the full-text path caps at 10,001. `resolveTarget` and
   * `confirmationFor` both take that seriously, and the dialog they produce for
   * a capped count REFUSES rather than showing a smaller number.
   */
  const match = useMemo(
    () => ({ total: walk.count?.value ?? walk.rows.length, exact: walk.count?.exact ?? true }),
    [walk.count, walk.rows.length],
  )
  const prompt = useMemo(
    () => (selection.selected.size > 0 ? escalationPrompt(walk.rows.length, match) : null),
    [selection.selected.size, walk.rows.length, match],
  )
  const confirmation = useMemo(
    () =>
      pending
        ? confirmationFor(pending.action, resolveTarget(selection, walk.rows, match), { queryWideOps: true })
        : null,
    [pending, selection, walk.rows, match],
  )

  /**
   * Is an `aria-modal` surface on screen?
   *
   * THE SHELL KEYMAP WAS NEVER SUPPRESSED, and that is a data-loss bug rather
   * than an annoyance: with the Screener open, `j` then `e` archived a row the
   * reader could not see, on the real mail server. Every overlay below declares
   * `aria-modal="true"`, which is a promise that the rest of the page is inert
   * — and the single-key command layer was the one thing still listening.
   *
   * ONE PREDICATE, AND EVERY TERM IS COPIED FROM THE OVERLAY'S OWN RENDER
   * GUARD, not paraphrased from it. `ConfirmDialog` renders on
   * `confirmation?.required === true` and NOT on `pending !== null` — the two
   * differ, and `pending` set with no dialog is a state whose only exit is the
   * Escape rung below, so suppressing there would strand it.
   *
   * THE CONSOLE IS DELIBERATELY ABSENT. It is `role="region"`, not a dialog; it
   * sits beside the list rather than over it, and it renders only when
   * `status?.available` — so keying off `consoleOpen` would deaden the whole
   * keyboard with nothing on screen for a household whose console is off.
   */
  const modalOpen =
    screenerOpen ||
    adminOpen ||
    rulesOpen ||
    settingsOpen ||
    // THE HELP SHEET IS ONE OF THESE, and it is the one that would be most
    // obviously wrong to leave out: it is a list of the keys, so a `j` typed
    // into its filter box scrolling the list behind it would be the surface
    // contradicting itself. It also OWNS `?` while it is open, which is what
    // lets a question mark be typed into the filter.
    helpOpen ||
    inspecting !== null ||
    draft !== null ||
    confirmation?.required === true

  /** Ask for an exact count, which is what a blocked confirmation needs. */
  const countExactly = useCallback(() => {
    api
      .search({ q: submitted, limit: 1, count: 'exact' })
      .then((page) => setWalk((live) => ({ ...live, count: page.count })))
      .catch(() => setNotice('Could not count that search exactly'))
  }, [submitted])

  /**
   * Apply a destructive action to every message the search matches.
   *
   * The list is re-read afterwards rather than patched. A whole-query move can
   * touch tens of thousands of rows, and reconciling that into the loaded page
   * would be a second, subtly different implementation of what the walk already
   * does correctly.
   */
  const runOverQuery = useCallback(
    (action: { action: string; special: string }, expect: number) => {
      const target = specialBox(mailboxes, action.special)
      if (!target) {
        setNotice(`Your mail server advertises no ${action.special} mailbox, so there is nowhere to put this`)
        return
      }
      api
        .opsOverQuery({ q: submitted, expect, op: 'move', to: target.name })
        .then((result) => {
          setNotice(
            result.failed > 0
              ? `${action.action}: ${result.applied} done, ${result.failed} refused by the mail server`
              : `${action.action}: ${result.applied.toLocaleString()} conversations`,
          )
          startWalk(submitted)
        })
        .catch((error: unknown) => {
          // A 409 is the honest case: mail arrived while the dialog was open, so
          // the number they confirmed is no longer the number that would move.
          setNotice(
            error instanceof HearthError && error.code === 'count_changed'
              ? 'More mail arrived while you were deciding, so nothing was changed. Try again to see the new count.'
              : describeError(error),
          )
          startWalk(submitted)
        })
    },
    [mailboxes, submitted, startWalk],
  )

  /**
   * Do one thing to a NAMED set of rows.
   *
   * SPLIT OUT OF `runCommand` SO A POINTER HAS SOMEWHERE TO GO. Every triage
   * path used to read `targets` — "the selection, else the cursor row" — which
   * is the right rule for a keystroke and has no answer for a button on a row
   * that is neither. The obvious workaround is worse than the gap: `setCursor`
   * then `runCommand` reads the PREVIOUS cursor, because `setSelection` is
   * asynchronous, so the click archives the message above the one under the
   * pointer. Naming the rows is what removes the question.
   *
   * `escalated` IS THE CALLER'S DECISION, deliberately not read from
   * `selection` here. Escalation is a property of a selection somebody built on
   * purpose and then confirmed; a row button means one row, and letting it see
   * `selection.escalated` would turn a single click into a move over every
   * message the query matches — tens of thousands of them.
   */
  const act = useCallback(
    (id: string, rows: readonly MessageRow[], escalated: boolean): boolean | void => {
      switch (id) {
        case 'archive':
          void triage.moveToSpecial(rows, '\\Archive', `Archived ${rows.length}`)
          return
        case 'trash':
        case 'spam': {
          // ESCALATED AND DESTRUCTIVE GOES THROUGH THE DIALOG. An ordinary
          // selection does not: a confirm on every delete trains people to
          // click through dialogs without reading, which is the failure the
          // dialog exists to prevent.
          const special = id === 'trash' ? '\\Trash' : '\\Junk'
          if (escalated) {
            setPending({ action: id, special })
            return
          }
          void triage.moveToSpecial(rows, special, `${id === 'trash' ? 'Deleted' : 'Reported'} ${rows.length}`)
          return
        }
        case 'star':
          void triage.flag(rows, ['\\Flagged'], [], `Starred ${rows.length}`)
          return
        case 'unstar':
          // NOT IN THE KEYMAP, and that is not an omission: `s` is a toggle at
          // the pointer because a star you can see is a star you expect to be
          // able to click off, and it is not one at the keyboard because a
          // selection of twelve rows has no single state to toggle.
          void triage.flag(rows, [], ['\\Flagged'], `Unstarred ${rows.length}`)
          return
        case 'read':
          void triage.flag(rows, ['\\Seen'], [], `Marked ${rows.length} read`)
          return
        case 'unread':
          void triage.flag(rows, [], ['\\Seen'], `Marked ${rows.length} unread`)
          return
        case 'snooze':
          // THE MOMENT, NOT THE MENU. `b` snoozes until tomorrow morning, which
          // is the answer a snooze menu is asked for most of the time — and a
          // key that opens a picker is a key that cannot be pressed twice in a
          // row down a list, which is the whole shape of triage here. A picker
          // belongs beside it later; this is the one that makes `b` mean
          // something today.
          void triage.snooze(rows, tomorrowMorning(), `Snoozed ${rows.length} until tomorrow`)
          return
        case 'unsnooze':
          void triage.snooze(rows, null, `Woke ${rows.length}`)
          return
        case 'replyLater':
          /**
           * THE KEYWORD, AND NOTHING MOVES. `interaction.md` gives `h` a docked
           * strip of avatars and `g h` a Focus & Reply screen with a composer
           * open on every stacked thread; this is the half of it that is true
           * today — the message is marked, it stays where it is, and `g h`
           * lists the marked ones like any other destination.
           *
           * THROUGH `flag` RATHER THAN THE `label` OP, deliberately. The origin
           * has both, and `label` is the one keyed by GUID that reaches every
           * location a message sits in — but `Triage` exposes only `flag`, and
           * the difference here is invisible: `message_keywords` is a UNION
           * over the locations, so `label:$replylater` finds the message either
           * way. A second mutation path routed straight at `api.ops` to get the
           * better op would lose the optimistic patch and the undo, which is a
           * real cost against a difference nothing can observe.
           */
          void triage.flag(rows, [REPLY_LATER_KEYWORD], [], `${rows.length} to reply to later`)
          return
        case 'unreplyLater':
          // NOT IN THE KEYMAP, for the reason `unstar` above is not: `h` on a
          // selection of twelve rows has no single state to toggle. The reading
          // pane's toolbar is where one message's mark comes off again.
          void triage.flag(rows, [], [REPLY_LATER_KEYWORD], `${rows.length} off the reply-later list`)
          return
        case 'mute':
        case 'unmute':
          void muteThreads(rows, id === 'mute')
          return
        default:
          return false
      }
    },
    [triage],
  )

  /**
   * One click on one row, whatever the selection happens to be.
   *
   * NEVER ESCALATED, and the selection is left exactly as it was: a button on a
   * row is not a statement about the reader's selection, so clearing it would
   * throw away work they did with the keyboard.
   */
  const actOnRow = useCallback(
    (id: string, row: MessageRow) => {
      act(id, [row], false)
    },
    [act],
  )

  /**
   * Go somewhere, which in this shell means: ask a different question.
   *
   * BOTH HALVES, ALWAYS. `submitted` is what the walk runs and `query` is what
   * the search box shows; setting only the first would leave the box saying
   * `in:INBOX` over a list of starred mail, and setting only the second would
   * show a query nobody ran. The nav does exactly this pair on a click.
   */
  const goTo = useCallback((next: string) => {
    setQuery(next)
    setSubmitted(next)
  }, [])

  /**
   * Go to the mailbox carrying a SPECIAL-USE flag.
   *
   * A NAMED REFUSAL WHEN THE SERVER ADVERTISES NONE, rather than a key that does
   * nothing: not every server publishes `\Drafts`, and "nothing happened" is
   * indistinguishable from a broken client. It is the same answer
   * `moveToSpecial` gives for the same missing flag, in the same words.
   */
  const goToSpecial = useCallback(
    (specialUse: string) => {
      const box = specialBox(mailboxes, specialUse)
      if (!box) {
        setNotice(`Your mail server advertises no ${specialUse} mailbox, so there is nowhere to go`)
        return
      }
      goTo(mailboxQuery(box.name))
    },
    [mailboxes, goTo],
  )

  /**
   * Read the labels when a label picker opens, and not before.
   *
   * ON DEMAND RATHER THAN ON MOUNT. It is one query against `message_keywords`
   * and nothing else in the shell wants the answer, so reading it every load
   * would be a request per session for a control most sessions never open — and
   * reading it once and caching it would offer a stale list to the reader most
   * likely to notice, the one who labelled something on their phone a minute
   * ago and is now reaching for it here.
   *
   * A FAILURE LEAVES THE LIST EMPTY AND THE PICKER OPEN. The box still takes a
   * new label, which is the half that always works; a toast about a list nobody
   * asked for would be an error message in front of somebody who is mid-gesture.
   */
  useEffect(() => {
    if (picking?.kind !== 'label') return
    let live = true
    api
      .labels()
      .then((answer) => {
        if (live && Array.isArray(answer.labels)) setLabels(answer.labels)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [picking])

  /**
   * Mute the conversations these rows belong to, or unmute them.
   *
   * THE SIEVE HALF GOES FIRST AND THE MESSAGES ONLY MOVE IF IT LANDED. A mute is
   * `$muted` plus a generated rule that catches the reply arriving tomorrow, and
   * the second half is the one that can fail — ManageSieve unconfigured, the mail
   * server refusing. Marking and archiving first would leave a reader looking at
   * a thread that says "muted" while every reply to it kept arriving, which is
   * the failure `m` was deleted from the keymap rather than ship.
   *
   * ONE REQUEST PER CONVERSATION AND NOT PER MESSAGE. `thread_id` is the root's
   * Message-ID, so twelve selected messages from one thread are one mute.
   *
   * THE FLAG AND THE MOVE STAY ORDINARY OPS, which is what gives them the
   * optimistic patch and the undo. Undoing puts the messages back and leaves the
   * rule in place — the same asymmetry an un-snooze has, and the honest one: the
   * way to stop muting is to unmute.
   */
  const muteThreads = useCallback(
    async (rows: readonly MessageRow[], on: boolean): Promise<void> => {
      const threads = [...new Set(rows.map((row) => row.thread_id).filter((id): id is string => !!id))]
      if (threads.length === 0) {
        setNotice('This conversation has no Message-ID, so there is nothing for a mute to match replies against')
        return
      }
      const done: string[] = []
      for (const thread of threads) {
        try {
          if (on) await api.mute(thread)
          else await api.unmute(thread)
          done.push(thread)
        } catch (error) {
          // THE ORIGIN'S OWN SENTENCE. It knows whether ManageSieve is absent,
          // whether the server refused, and which; a local rewording here would
          // be this client having an opinion about a rule it does not hold.
          setNotice(error instanceof Error ? error.message : 'That conversation could not be muted')
          break
        }
      }
      if (done.length === 0) return
      const affected = rows.filter((row) => row.thread_id && done.includes(row.thread_id))
      if (on) {
        await triage.flag(affected, [MUTED_KEYWORD], [], `Muted ${many(done.length, 'conversation')}`)
        await triage.moveToSpecial(affected, ARCHIVE_SPECIAL_USE, `Muted ${many(done.length, 'conversation')}`)
      } else {
        await triage.flag(affected, [], [MUTED_KEYWORD], `Unmuted ${many(done.length, 'conversation')}`)
      }
    },
    [triage],
  )

  const runCommand = useCallback(
    (id: string): boolean | void => {
      const rows = walk.rows
      switch (id) {
        case 'cursor.down':
          setPermalinkRow(null)
          setSelection((state) => moveCursor(state, 1, rows))
          return
        case 'cursor.up':
          setPermalinkRow(null)
          setSelection((state) => moveCursor(state, -1, rows))
          return
        case 'select.toggle':
          setSelection((state) => toggleAtCursor(state, rows))
          return
        // TIER 3, the `*` chords. The ids come from `keymap.ts`; guessing them
        // is how a binding silently does nothing.
        case 'sel.all':
          setSelection((state) => selectAllLoaded(state, rows))
          return
        case 'sel.none':
          setSelection(clearSelection)
          return
        case 'sel.read':
          setSelection((state) => selectWhere(state, rows, (r) => !isUnread((r as MessageRow).flags)))
          return
        case 'sel.unread':
          setSelection((state) => selectWhere(state, rows, (r) => isUnread((r as MessageRow).flags)))
          return
        case 'sel.starred':
          setSelection((state) => selectWhere(state, rows, (r) => isFlagged((r as MessageRow).flags)))
          return
        case 'search':
          searchRef.current?.focus()
          searchRef.current?.select()
          return
        case 'open':
          // NOTHING TO OPEN IS NOT AN ERROR, and it must not put the shell into
          // the reading state with an empty pane — which under 60rem would hide
          // the list behind nothing at all.
          if (!cursorRow) return
          setReading(true)
          // AND FOCUS GOES WITH IT. Without this, Enter opened a conversation
          // the keyboard could then do nothing with: `n`, `p` and `o` live on
          // the conversation's own container and only fire when focus is inside
          // it, and `a` (reply-all) is a `thread` binding.
          setFocusSignal((n) => n + 1)
          return
        case 'back': {
          setReading(false)
          setPermalinkRow(null)
          // LEAVING THE MESSAGE LEAVES ITS KEYMAP. Focus is what decides the
          // context, so a `u` pressed with focus still on a message header would
          // hide the pane and leave `x` inert with the list back on screen.
          // Asked of the DOM rather than tracked: `inMessage` is the same fact
          // one render later, and this runs before that render.
          const here = document.activeElement
          if (here instanceof HTMLElement && readPane.current?.contains(here)) here.blur()
          return
        }
        case 'go.settings':
          setSettingsOpen(true)
          return
        case 'escape':
          // NO `settingsOpen` RUNG HERE. Settings binds its own Escape while it
          // is open (`Settings.tsx`), and the shell keymap is suppressed under
          // any modal anyway — a second predicate about "is Settings open"
          // would only be a place for the two to disagree.
          if (pending) {
            setPending(null)
            return
          }
          // THE ESC LADDER, in `interaction.md`'s order. Only the rungs that
          // exist yet: dismiss the notice, then clear the selection. Each
          // press does exactly one thing, which is what makes it predictable.
          if (notice) setNotice(null)
          else if (selection.selected.size > 0) setSelection(clearSelection)
          return
        case 'archive':
          // ARCHIVE HAS NO QUERY-WIDE PATH and never had one — `escalate` is
          // offered for it, and `e` still moves only the loaded rows. That
          // asymmetry is older than this refactor and is left exactly as it
          // was rather than quietly changed here. `rows.length` is its own
          // empty-list guard, also unchanged.
          act('archive', rows.length ? targets : [], false)
          setSelection(clearSelection)
          return
        case 'archiveNext':
        case 'archivePrev': {
          /**
           * `e` AND A CURSOR STEP — the rhythm key, which is the whole of
           * one-handed triage: file this, look at the next one, decide again,
           * without the hand leaving the two brackets.
           *
           * DERIVED FROM `e` RATHER THAN SPELLED AGAIN: the same target rule,
           * the same op, the same clear. If archiving ever changes, it changes
           * for these too.
           *
           * THE STEP IS TAKEN OVER THE LIST THE ARCHIVE IS ABOUT TO LEAVE
           * BEHIND, which is not the same as stepping the cursor. `moveCursor`
           * clamps, so `[` on the top row lands back on the row being archived
           * — and that id is gone a moment later, leaving no cursor at all and
           * an empty reading pane. Against the rows that will remain, "the next
           * one" is simply the row that takes this one's place, and "the
           * previous one" is the row before that; both clamp into a list that
           * still exists.
           */
          if (targets.length === 0) return
          const gone = new Set(targets.map((target) => target.id))
          const at = rows.findIndex((candidate) => candidate.id === cursorRow?.id)
          const left = rows.filter((candidate) => !gone.has(candidate.id))
          act('archive', targets, false)
          setSelection((state) => setCursor(clearSelection(state), id === 'archiveNext' ? at : at - 1, left))
          return
        }
        case 'mute':
          // THE SELECTION CLEARS, because the rows leave: a mute archives what
          // is already here, exactly as the reader would have done by hand.
          if (targets.length === 0) return
          act('mute', targets, false)
          setSelection(clearSelection)
          return
        case 'replyLater':
          // NO CLEAR AFTERWARDS, unlike archive: nothing left the list, so the
          // selection the reader built is still about the rows in front of them.
          act('replyLater', targets, false)
          return
        case 'trash':
        case 'spam':
          act(id, targets, selection.escalated)
          // THE ESCALATED PATH IS THE DIALOG'S TO FINISH: its Confirm and
          // Cancel both clear the selection, and clearing it here would take
          // the rows out from under the question it is about to ask.
          if (!selection.escalated) setSelection(clearSelection)
          return
        case 'star':
        case 'read':
        case 'unread':
          act(id, targets, false)
          return
        case 'snooze':
          // THE SAME EMPTY-LIST GUARD ARCHIVE USES, and for the same reason: a
          // key pressed with nothing under the cursor must do nothing rather
          // than send an op with no targets.
          act('snooze', targets.length ? targets : [], false)
          setSelection(clearSelection)
          return
        /**
         * `l` AND `v` ASK A QUESTION FIRST, which is why they are the only two
         * action keys here that open something instead of doing something.
         *
         * Both were declared in the keymap, resolved to nothing, and were
         * DELETED with the reason written into `keymap.ts`: a label and a
         * destination are answers a person has to give, and a key that opened
         * nothing to give it in was worse than an absent key. The rows are
         * captured NOW rather than read again when the picker answers — the
         * cursor moves, the selection clears, and the messages the reader meant
         * are the ones that were under the key when they pressed it.
         */
        case 'label':
          if (targets.length === 0) return
          setPicking({ kind: 'label', rows: targets })
          return
        case 'move':
          if (targets.length === 0) return
          setPicking({ kind: 'move', rows: targets })
          return
        case 'undo':
          void triage.undo()
          return
        case 'geek.inspector':
          if (!cursorRow) {
            setNotice('Open a message first')
            return
          }
          setInspecting(cursorRow)
          return
        case 'geek.console':
          // A TOGGLE, because the console is a persistent surface rather than a
          // dialog: somebody types a command, reads the answer against the list
          // beside it, and types another. Reopening it on every press would
          // throw the transcript away.
          setConsoleOpen((open) => !open)
          return
        case 'go.rules':
          setRulesOpen(true)
          return
        case 'compose':
          setDraft({ mode: 'new' })
          return
        case 'reply':
        case 'replyAll':
        case 'forward': {
          // THE MESSAGE IF THEY ARE IN ONE, ELSE THE ROW. Both halves are read
          // from where focus is rather than from what exists: `inThread` outlives
          // the reader leaving the pane by one render, and answering the wrong
          // message is exactly the failure this is here to prevent.
          const row = (inMessage ? inThread : null) ?? cursorRow
          if (!row) {
            setNotice('Open a message first')
            return
          }
          // THE BODY IS THE COMPOSER'S TO FETCH. It lives in the reading pane's
          // own state, and threading it through the shell to get here would
          // couple two components that otherwise share nothing but a row.
          setDraft({
            mode: id === 'reply' ? 'reply' : id === 'replyAll' ? 'reply-all' : 'forward',
            inReplyTo: row,
          })
          return
        }
        // TIER 2 — every destination is a QUERY, which is the shell's whole
        // premise (see the file header) and the reason six of these needed no
        // new machinery at all. `INBOX` is the one mailbox name IMAP fixes, so
        // it is the one that can be written down; the rest are resolved by
        // SPECIAL-USE or expressed as a search.
        case 'go.inbox':
          goTo('in:INBOX')
          return
        case 'go.starred':
          // A SEARCH AND NOT A FOLDER, deliberately: `\Flagged` is per-location,
          // so `is:starred` finds the starred copy wherever it is sitting —
          // which `interaction.md` records as the honest consequence of stars
          // being an IMAP flag rather than a Gmail label.
          goTo('is:starred')
          return
        case 'go.sent':
          goToSpecial('\\Sent')
          return
        case 'go.drafts':
          goToSpecial('\\Drafts')
          return
        case 'go.all':
          goTo(ALL_MAIL_QUERY)
          return
        case 'go.snoozed':
          // REAL AS OF `b`: the snooze op writes `snoozed_until` on the message,
          // which is exactly the column `is:snoozed` compiles against — so this
          // destination lists what the snooze key parked and nothing else. It
          // is also the only way to see a snoozed message before it wakes, and
          // "a snoozed message that has silently vanished with no way back is a
          // trust bug" is `interaction.md`'s own words.
          goTo('is:snoozed')
          return
        case 'go.later':
          goTo(REPLY_LATER_QUERY)
          return
        case 'help':
        case 'palette':
          // BOTH KEYS, ONE SURFACE. See `keymap.ts` beside the two bindings for
          // why that is a decision and not a stub: every command here is a bound
          // command, so the list of bindings IS the palette.
          setHelpOpen(true)
          return
        default:
          if (id.startsWith('blocked:')) {
            setNotice('Your account does not have permission for that')
            return
          }
          // Unhandled bindings keep their key: `c` should type a `c` until
          // there is a composer, rather than silently swallowing it.
          return false
      }
    },
    [walk.rows, targets, triage, notice, selection, pending, cursorRow, act, inMessage, inThread, goTo, goToSpecial],
  )

  useKeyboard({
    /**
     * WHERE FOCUS ACTUALLY IS, and nothing else.
     *
     * The first attempt at this switched to `thread` whenever a cursor row
     * existed, which broke selection immediately: `x` is a `list` binding and
     * the cursor is set by every search, so the context was `thread` from the
     * first paint. A cursor is not a modal view. The keymap's answer to that was
     * to give `reply` and `forward` a `list` meaning as well, and it is still
     * right — the pane and the list are on screen together.
     *
     * What it could not answer is `a`. `replyAll` is deliberately `thread`-only
     * (a bare `a` is already `* a` and `g a`), and `Context = 'thread'` was
     * therefore fiction: nothing ever produced it, so the binding could not
     * fire. Focus is the fact that makes it real, and it is a fact about the
     * reader rather than about the data — it becomes true when they press Enter
     * or click into the message and false again when they leave.
     */
    context: inMessage ? 'thread' : 'list',
    onCommand: runCommand,
    // AN OPEN DIALOG OWNS THE KEYBOARD. See `modalOpen` above for why this is
    // one boolean rather than a rung in the Escape ladder.
    enabled: !modalOpen,
    ...(capabilities ? { capabilities } : {}),
    // THE STORED REMAPPING, which had no way in until now. `useKeyboard`
    // rebuilds its resolver when this object changes, so the read landing a
    // moment after mount is handled without anything here waiting for it.
    overrides: overrides.byId,
  })

  return (
    <div
      className="shell"
      data-reading={reading ? 'true' : 'false'}
      // THE LAYOUT SETTING, AS AN ATTRIBUTE THE CSS CAN SEE. `none` is
      // deliberately unstyled: `review.md` found that hiding the pane with a
      // rule alone leaves the documented, deep-linkable thread route with
      // nowhere to render, and that is a design decision to take rather than a
      // rule to add.
      data-pane={readingPrefs.readingPane}
    >
      <div className="pane-search">
        <form
          style={{ display: 'contents' }}
          onSubmit={(event) => {
            event.preventDefault()
            setSubmitted(query)
          }}
        >
          <input
            ref={searchRef}
            className="search-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search — from:anna is:unread invoice"
            aria-label="Search mail"
            // NO `autoFocus`, AND THAT IS THE OPPOSITE OF WHAT IT LOOKS LIKE.
            // Focusing the search box on load puts the caret in a text field,
            // which is exactly where the keyboard map is deliberately inert —
            // so `j`, `k` and `e` did nothing at all until the reader thought
            // to click elsewhere. Search-first means search is ONE KEYSTROKE
            // away (`/`), not that it holds focus. The list takes focus below.
            spellCheck={false}
          />
        </form>
        <span className="search-count">{walk.loading && walk.rows.length === 0 ? '…' : countLabel(walk.count)}</span>
        {/* THREADS OR MESSAGES, in the header rather than in Settings.
            `decisions.md` asks that it be selectable because "some like it this
            way, some the other" — which is a thing people flip while reading,
            not a thing they configure once. A preference you change often
            belongs where you are when you change it.

            It is a real toggle button with `aria-pressed`, not a pair of tabs:
            there are two states, one control, and a screen reader should hear
            which one is on. */}
        <button
          type="button"
          className="group-toggle"
          aria-pressed={grouping === 'threads'}
          title={
            grouping === 'threads'
              ? 'Showing conversations. Click for individual messages.'
              : 'Showing individual messages. Click for conversations.'
          }
          onClick={() => {
            const next: Grouping = grouping === 'threads' ? 'messages' : 'threads'
            // APPLIED FIRST, PERSISTED AFTER. The list re-runs on the state
            // change; a write that fails leaves the reader looking at the
            // grouping they asked for and told that it will not be remembered,
            // which is both true and the more useful half.
            setGrouping(next)
            writeGrouping(next).catch(() =>
              setNotice('Switched for now — that preference could not be saved.'),
            )
          }}
        >
          {grouping === 'threads' ? 'Conversations' : 'Messages'}
        </button>
        {/* HONEST ABOUT THE CONNECTION rather than silently stale. `open` shows
            nothing at all — a green light for the normal case is noise. */}
        {push.state === 'reconnecting' ? (
          <span className="push-state" title={`${push.attempts} attempts`}>
            Reconnecting…
          </span>
        ) : null}
        {/* COMPOSE HAS ONLY EVER BEEN `c`. A mail client whose only way to write
            a message is a single keystroke nobody was told about is a mail
            client that cannot send mail. Gated on `mail.send` the same way the
            binding is — see `may`. */}
        {may('mail.send') ? (
          <button
            type="button"
            className="icon-button"
            aria-label="Write a message (c)"
            title="Write a message (c)"
            onClick={() => setDraft({ mode: 'new' })}
          >
            ✎
          </button>
        ) : null}
        {/* THE ONLY WAY IN THAT IS NOT A KEY. `?` opens the sheet, and a person
            who does not know the keyboard exists is exactly the person who
            cannot press it — a discoverability surface reachable only by the
            thing it teaches is not one. */}
        <button
          type="button"
          className="icon-button"
          aria-label="Keyboard help (?)"
          title="Keyboard help (?)"
          onClick={() => setHelpOpen(true)}
        >
          ?
        </button>
        <button
          type="button"
          className="icon-button"
          aria-label="Settings"
          onClick={() => setSettingsOpen(true)}
        >
          ⚙
        </button>
      </div>

      <nav className="pane-nav" aria-label="Destinations">
        <Destinations mailboxes={mailboxes} current={submitted} onPick={(q) => { setQuery(q); setSubmitted(q) }} />
        {/* A BUTTON IN THE RAIL AND NOT A QUERY, because the Screener is not a
            search: it is a list of senders, and the shell's destinations all
            resolve to a query over messages. Keeping it out of `Destinations`
            is what stops it pretending to be one. */}
        {/* ANY OF THE THREE ADMIN CAPABILITIES, and all three are tailnet-only —
            so the same person has no admin entry at all on the public path.
            `/whoami` reports the capability set the ORIGIN resolved, including
            that restriction, so this needs no second copy of the rule.
            IT USED TO BE `admin.server` ALONE, which made the other two
            unreachable: an origin that had learned to tell `admin.accounts` and
            `admin.logs` apart still had one screen, behind the capability that
            grants everything. The panel shows the tabs the reader holds. */}
        {ADMIN_CAPABILITIES.some((capability) => capabilities?.has(capability)) ? (
          <button className="nav-item nav-admin" aria-current={adminOpen} onClick={() => setAdminOpen(true)}>
            <span>Server</span>
          </button>
        ) : null}
        {/* THE OUTBOX, and only when there is something in it or the reader can
            send at all. A permanently empty tray in the rail is furniture; a
            tray with a red count on it is the only place a failed send is ever
            mentioned. */}
        {capabilities?.has('mail.send') && (failedSends > 0 || outboxOpen) ? (
          <button className="nav-item nav-outbox" aria-current={outboxOpen} onClick={() => setOutboxOpen(true)}>
            <span>Outbox</span>
            {failedSends > 0 ? <span className="nav-badge nav-badge-bad">{failedSends}</span> : null}
          </button>
        ) : null}
        {waiting?.ready ? (
          <button
            className="nav-item nav-screener"
            aria-current={screenerOpen}
            onClick={() => setScreenerOpen(true)}
          >
            <span>Screener</span>
            {waiting.senders > 0 ? <span className="nav-badge">{waiting.senders}</span> : null}
          </button>
        ) : null}
      </nav>

      <div className="pane-list">
        {/* WHY THE INBOX LOOKS LIKE THIS, where the reader is already looking.
            `review.md`'s remedy for the Screener blocker is not only the seed —
            it is that an empty Inbox is never unexplained. A count sitting only
            behind a destination somebody has to think to visit does not do
            that. Rendered only when there IS somebody waiting, so it is not a
            permanent strip of furniture. */}
        {waiting && waiting.ready && waiting.senders > 0 ? (
          <button type="button" className="screener-banner" onClick={() => setScreenerOpen(true)}>
            {waiting.senders === 1
              ? '1 new sender is waiting in the Screener'
              : `${waiting.senders} new senders are waiting in the Screener`}
          </button>
        ) : null}
        {/* WHAT THE ORIGIN COULD NOT DO EXACTLY, above the rows it is about.
            The origin emits these and the client dropped every one, which turns
            "this list is incomplete, and here is why" into a short list that
            looks complete — the same failure the notes were added to fix. The
            sentence is the ORIGIN's: it knows which term was floored and by how
            much, and a copy of that wording here would be one edit away from
            describing a different limit than the one that fired. */}
        {walk.notes.map((note) => (
          <p key={`${note.code} ${note.term}`} className="search-note" role="status">
            {note.message}
          </p>
        ))}
        <EscalationBanner
          prompt={prompt}
          escalated={selection.escalated}
          onEscalate={() => setSelection(escalate)}
          onClear={() => setSelection(clearSelection)}
        />
        <MessageList
          walk={walk}
          selection={selection}
          onSelection={setSelection}
          onOpen={() => setReading(true)}
          onAct={actOnRow}
          canWrite={may('mail.write')}
          outgoing={outgoing}
          onMore={loadMore}
          pageSize={pageSize}
        />
      </div>

      {/* `onFocus`/`onBlur` ARE focusin/focusout HERE — React's do bubble, which
          is the whole reason this can sit on the pane rather than on every
          control inside it. The `relatedTarget` check is what stops a move
          BETWEEN two message headers reading as leaving the pane: focusout fires
          before focusin, so without it the context would flicker back to `list`
          in between, and a key pressed at that moment would go to the list.

          ONLY A MESSAGE COUNTS AS BEING IN THE MESSAGE, and that is the whole of
          the `data-thread-focusable` test. Because focusin bubbles, the first
          version treated focus ANYWHERE in this pane as "the reader is in the
          conversation" — including the archive, delete, star, reply and Copy
          link buttons of the toolbar, which live in here. So clicking any of
          them put the shell in the `thread` context, where `x`, all five `*`
          selectors and Enter are list-only, and they went dead until the reader
          clicked back into the list or pressed `u`. It recovered from nothing on
          its own, and when the focused button unmounted — archiving the last row
          empties the pane — focusout is not reliably delivered and it could
          stick with no message on screen at all.

          The conversation's own message headers carry the attribute; a toolbar
          button does not. */}
      <div
        className="pane-read"
        ref={readPane}
        onFocus={(event) => {
          if ((event.target as HTMLElement).closest('[data-thread-focusable]')) setInMessage(true)
        }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setInMessage(false)
          else if (!(event.relatedTarget as HTMLElement | null)?.closest('[data-thread-focusable]')) {
            setInMessage(false)
          }
        }}
      >
        <ReadingPane
          row={cursorRow}
          prefs={readingPrefs}
          onMarkRead={markRead}
          onBack={() => runCommand('back')}
          onAct={actOnRow}
          onCommand={runCommand}
          canWrite={may('mail.write')}
          canSend={may('mail.send')}
          focusSignal={focusSignal}
          onFocusedMessage={setInThread}
        />
      </div>

      {notice ? (
        <div className="notice" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      <Toast toast={triage.toast} onUndo={() => void triage.undo()} onDismiss={triage.dismissToast} />
      <Console open={consoleOpen} onClose={() => setConsoleOpen(false)} />
      <Admin
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        onError={setNotice}
        // WHICH TABS, DECIDED FROM THE SAME ANSWER THE NAV ENTRY IS. The three
        // admin capabilities are three powers and the origin has told them apart
        // since the verb table landed; passing the set through is what makes
        // that true on screen as well.
        {...(capabilities ? { capabilities } : {})}
      />
      <Screener
        open={screenerOpen}
        onClose={() => setScreenerOpen(false)}
        onError={setNotice}
        // A DECISION CHANGES WHERE MAIL LANDS, so the list is stale the moment
        // one is made — and so is the count in the banner beside it.
        onDecided={() => {
          refreshScreener()
          startWalk(submitted)
        }}
      />
      <Outbox
        open={outboxOpen}
        onClose={() => {
          setOutboxOpen(false)
          refreshOutbox()
        }}
        onError={setNotice}
        load={api.outbox}
        unsend={api.unsend}
      />
      <Inspector row={inspecting} onClose={() => setInspecting(null)} onError={setNotice} />
      <Rules
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        mailboxes={mailboxes}
        onError={setNotice}
      />
      <Compose
        draft={draft}
        me={whoami?.account}
        onClose={() => setDraft(null)}
        onQueued={setQueued}
        onError={setNotice}
      />
      {queued && (
        <div className="send-toast" role="status">
          <span>Message will send in {Math.max(0, queued.sendAfter - Math.floor(Date.now() / 1000))}s</span>
          <button
            type="button"
            onClick={() => {
              const id = queued.id
              setQueued(null)
              api
                .unsend(id)
                .then(() => setNotice('Message brought back'))
                .catch((error: unknown) =>
                  // `too_late` is not a failure: the message went, which is what
                  // the reader asked for a moment ago.
                  setNotice(
                    error instanceof HearthError && error.code === 'too_late'
                      ? 'That message has already gone'
                      : describeError(error),
                  ),
                )
            }}
          >
            Undo
          </button>
        </div>
      )}

      {/* THE ANSWER TO `l` AND `v`. It is rendered here rather than beside the
          list because it is a modal question about rows that are already
          decided — see `picking`, which holds them — and because Escape has to
          reach it before it reaches anything else on the page. */}
      {picking ? (
        <Picker
          title={picking.kind === 'label' ? `Label ${many(picking.rows.length, 'message')}` : `Move ${many(picking.rows.length, 'message')} to`}
          placeholder={picking.kind === 'label' ? 'invoices' : 'Archive'}
          allowNew={picking.kind === 'label'}
          options={
            picking.kind === 'label'
              ? labels.map((one) => ({ value: one.label, detail: one.messages.toLocaleString() }))
              : mailboxes
                  .filter((box) => box.name !== picking.rows[0]?.mailbox)
                  .map((box) => ({ value: box.name, ...(box.specialUse ? { detail: box.specialUse } : {}) }))
          }
          onClose={() => setPicking(null)}
          onChoose={(value) => {
            const chosen = picking
            setPicking(null)
            setSelection(clearSelection)
            if (chosen.kind === 'label') {
              void triage.label(chosen.rows, [value], [], `Labelled ${many(chosen.rows.length, 'message')} ${value}`)
            } else {
              void triage.moveTo(chosen.rows, value, `Moved ${many(chosen.rows.length, 'message')} to ${value}`)
            }
          }}
        />
      ) : null}
      <Settings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onPageSize={setPageSize}
        // THE ONE COPY OF `/whoami`, read on mount and handed down. The
        // Addresses section inside used to fetch its own on every open: a round
        // trip for an answer already here, and two copies of one fact that can
        // be read at different moments and disagree about what somebody may do.
        me={whoami}
      />
      <Help
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        // THE RESOLVER'S OWN ARRAY, remaps applied. See `bindings` above.
        bindings={bindings}
        {...(capabilities ? { capabilities } : {})}
        // STRAIGHT TO `runCommand`, which is the same door the keyboard uses —
        // so a command cannot work from one and not the other, and a binding
        // with no arm is as visibly dead in the palette as it is under the key.
        onRun={runCommand}
        target={
          selection.selected.size > 0
            ? `${many(selection.selected.size, 'message')} selected`
            : (cursorRow?.subject ?? null)
        }
        ignoredOverrides={overrides.ignored}
      />
      <ConfirmDialog
        confirmation={confirmation}
        action={pending?.action ?? ''}
        onCancel={() => setPending(null)}
        onCountExactly={countExactly}
        onConfirm={() => {
          if (pending) {
            // THE WHOLE QUERY, WHICH IS WHAT THE DIALOG SAID. The escalated path
            // used to send one operation per LOADED ROW while the copy promised
            // every match — a hundred of forty thousand, confirmed once and
            // silently smaller. `expect` is the number the reader just agreed
            // to, and the origin refuses with a 409 if mail arrived meanwhile.
            const wanted = match.total
            void runOverQuery(pending, wanted)
            setSelection(clearSelection)
          }
          setPending(null)
        }}
      />
    </div>
  )
}

/**
 * A thrown thing, as a sentence.
 *
 * NAMED `describeError` RATHER THAN `describe` because it is exported now — the
 * conversation view reports its own failed walk — and `describe` is what every
 * test file in this repo calls vitest's own global. One import away from a very
 * confusing shadow.
 */
export function describeError(error: unknown): string {
  if (error instanceof HearthError) return error.message
  return error instanceof Error ? error.message : 'Something went wrong'
}

/**
 * The left nav.
 *
 * EVERY ENTRY IS A QUERY. `in:INBOX` rather than an "open this folder" action,
 * because that is what makes a destination and a saved search the same object —
 * and because the list pane then has exactly one way to be filled.
 *
 * SPECIAL-USE, NEVER THE NAME. Dovecot advertises `\\Archive`, `\\Sent`,
 * `\\Junk` and `\\Trash` as flags; matching on the literal string "Archive"
 * breaks on every server whose owner renamed it, and on every language that is
 * not English. The name is what goes in the query because that is what IMAP
 * takes; the FLAG is what decides the order and the label.
 */
function Destinations({
  mailboxes,
  current,
  onPick,
}: {
  mailboxes: readonly MailboxState[]
  current: string
  onPick: (query: string) => void
}): React.JSX.Element {
  const ordered = useMemo(() => {
    const rank = (box: MailboxState): number => {
      const use = box.specialUse ?? ''
      const order = ['\\Inbox', '\\Drafts', '\\Sent', '\\Archive', '\\Junk', '\\Trash']
      const found = order.indexOf(use)
      return found === -1 ? order.length : found
    }
    return [...mailboxes].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
  }, [mailboxes])

  return (
    <>
      <button className="nav-item" aria-current={current === 'is:unread'} onClick={() => onPick('is:unread')}>
        <span>Unread</span>
      </button>
      {ordered.map((box) => {
        const query = mailboxQuery(box.name)
        return (
          <button key={box.name} className="nav-item" aria-current={current === query} onClick={() => onPick(query)}>
            <span>{box.name}</span>
          </button>
        )
      })}
    </>
  )
}

/**
 * The list.
 *
 * VIRTUALISED AT A FIXED ROW HEIGHT, which is the whole reason the archive can
 * be scrolled at all: 369,000 rows is not a thing the DOM holds. The height
 * comes from `--row-h` resolved once — see `rowHeight.ts` for why it cannot be
 * a constant and cannot be measured per row.
 *
 * THE CURSOR IS A ROW ID, NEVER AN INDEX. `@tanstack/react-virtual` recycles
 * DOM nodes as they leave the viewport, so an index-keyed cursor lands on
 * whatever row later occupies that slot — and the change feed shifts every index
 * below an insertion anyway. `selection.ts` records this as a fixed defect.
 */
function MessageList({
  walk,
  selection,
  onSelection,
  onOpen,
  onAct,
  canWrite,
  outgoing,
  onMore,
  pageSize,
}: {
  walk: Walk
  selection: SelectionState
  onSelection: (next: (state: SelectionState) => SelectionState) => void
  /** A click on a row means "open this", which a cursor move does not. */
  onOpen: () => void
  /** One action on ONE named row, never on the selection. See `act`. */
  onAct: (id: string, row: MessageRow) => void
  /** Already resolved from `/whoami`; `undefined` means ungated, not blocked. */
  canWrite: boolean
  /** Mailbox NAMES carrying `\\Sent` or `\\Drafts`. See `correspondent`. */
  outgoing: ReadonlySet<string>
  onMore: () => void
  pageSize: number
}): React.JSX.Element {
  const cursorId = selection.cursorId
  const scrollRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const [rowHeight, setRowHeight] = useState(() => readRowHeight())

  useEffect(() => watchRowHeight(setRowHeight), [])

  // THE LIST OWNS FOCUS ON ARRIVAL, so the keyboard works without the reader
  // having to click anything. `interaction.md` makes the grid one tab stop with
  // a roving cursor; this is the other half of that — something has to hold the
  // stop, and if the search box does, every single-key binding is suppressed.
  const focused = useRef(false)
  useEffect(() => {
    if (focused.current || walk.rows.length === 0) return
    focused.current = true
    gridRef.current?.focus({ preventScroll: true })
  }, [walk.rows.length])

  const virtualizer = useVirtualizer({
    count: walk.rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  })

  /**
   * NO SCROLL TRIGGER. There used to be one, and having it alongside a page
   * size meant the size did not mean anything: set it to 100 and the list kept
   * fetching as you moved down. Loading on scroll and choosing a page size are
   * two answers to the same question, and this is the one that was asked for.
   */
  const items = virtualizer.getVirtualItems()

  /**
   * FOLLOW THE CURSOR. `j` past the bottom of the viewport moved the cursor and
   * left the viewport alone, so the row with the focus ring on it was somewhere
   * off screen — in a virtualised list that also means it is not in the DOM at
   * all, and `aria-activedescendant` pointed at a node that did not exist.
   *
   * KEYED ON THE CURSOR ID, NOT ON THE SELECTION. Ticking a checkbox produces a
   * new selection object and must not yank the viewport; neither must a "Load
   * more" that appends rows. `align: 'auto'` is the one that leaves an
   * already-visible row where it is, which is what makes the mount-time
   * cursor-to-row-0 a no-op rather than a scroll.
   */
  const cursorAt = useMemo(() => walk.rows.findIndex((r) => r.id === cursorId), [walk.rows, cursorId])
  useEffect(() => {
    if (cursorAt >= 0) virtualizer.scrollToIndex(cursorAt, { align: 'auto' })
  }, [cursorAt, virtualizer])

  if (walk.error) {
    return (
      <div className="state" data-kind="error">
        <span className="state-title">That search did not run</span>
        <span>{walk.error}</span>
      </div>
    )
  }
  if (walk.loading && walk.rows.length === 0) return <div className="state">Searching…</div>
  if (walk.rows.length === 0) {
    return (
      <div className="state">
        <span className="state-title">Nothing matches</span>
        <span>No message in your mail matches this search.</span>
      </div>
    )
  }

  return (
    <div className="list-scroll" ref={scrollRef}>
      <div
        ref={gridRef}
        className="list-grid"
        role="grid"
        aria-rowcount={walk.count?.exact ? walk.count.value : -1}
        aria-activedescendant={cursorId ? `row-${cursorId}` : undefined}
        tabIndex={0}
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {items.map((item) => {
          const row = walk.rows[item.index]
          if (!row) return null
          return (
            <div
              key={row.id}
              id={`row-${row.id}`}
              className="row"
              role="row"
              aria-selected={selection.selected.has(row.id) || row.id === cursorId}
              data-cursor={row.id === cursorId}
              data-selected={selection.selected.has(row.id)}
              data-unread={isUnread(row.flags)}
              style={{ transform: `translateY(${item.start}px)` }}
              onClick={(event) => {
                // Shift-click extends from the anchor, which is the mouse
                // spelling of the same range the keyboard builds with Shift+J.
                onSelection((state) =>
                  event.shiftKey ? extendTo(state, item.index, walk.rows) : setCursor(state, item.index, walk.rows),
                )
                // A PLAIN CLICK OPENS; A SHIFT-CLICK IS BUILDING A RANGE and
                // must not throw the reader into the message on a phone.
                if (!event.shiftKey) onOpen()
              }}
            >
              <input
                className="row-check"
                type="checkbox"
                checked={selection.selected.has(row.id)}
                aria-label={`Select ${row.subject ?? 'message'}`}
                onClick={(event) => event.stopPropagation()}
                onChange={() =>
                  onSelection((state) => toggleAtCursor(setCursor(state, item.index, walk.rows), walk.rows))
                }
              />
              <span className="row-from">
                {correspondent(row, outgoing)}
                {/* ONLY WHEN THERE IS MORE THAN ONE, and only on a conversation
                    page — the field is absent on a message page, so this cannot
                    render "1" for mail nobody counted. A count of exactly 1 is
                    also worth hiding: a badge on every row would carry no
                    information and would push the sender's name off the line. */}
                {row.thread_count !== undefined && row.thread_count > 1 ? (
                  <span className="row-thread-count" aria-label={`${row.thread_count} messages in this conversation`}>
                    {row.thread_count}
                  </span>
                ) : null}
              </span>
              <span className="row-subject">
                {/* THREE FIELDS THE WIRE ALREADY CARRIED AND NOTHING DREW.
                    `s` posted `\Flagged`, said "Starred 1" and changed nothing
                    on screen — and `* s` filtered on a property the reader
                    could not see. `has_attachment` was on every row and drawn
                    nowhere, so "which of these has the invoice" meant opening
                    each one. Both ride inside the subject cell rather than
                    taking a column, because the row's grid is what the
                    virtualiser's fixed height is built on. */}
                {isFlagged(row.flags) ? (
                  <span className="row-marker" data-kind="star" role="img" aria-label="Starred">
                    ★
                  </span>
                ) : null}
                {/* `has_attachment` is 0 | 1 on the wire, not a boolean. */}
                {row.has_attachment > 0 ? (
                  <span className="row-marker" data-kind="clip" role="img" aria-label="Has an attachment">
                    ⏚
                  </span>
                ) : null}
                {row.subject || '(no subject)'}
                {row.preview ? <span className="row-preview"> — {row.preview}</span> : null}
              </span>
              <span className="row-tail">
                <span className="row-date">{listDate(row.date)}</span>
                {/* THE POINTER PATH. Every action here was reachable by one key
                    and by nothing else — a client with no mouse affordance for
                    archiving is a client only its author can use. The cluster
                    sits in the SAME grid cell as the date and is revealed on
                    hover, so it costs no column and the row never reflows.

                    `stopPropagation` on each, the way `.row-check` already
                    does: without it, clicking Archive also moves the cursor and
                    opens the message it has just filed away. */}
                {canWrite ? (
                  <span className="row-actions">
                    <RowAction row={row} id="archive" keyName="e" label="Archive" glyph="→" onAct={onAct} />
                    <RowAction row={row} id="trash" keyName="#" label="Delete" glyph="␡" onAct={onAct} />
                    <RowAction
                      row={row}
                      id={isFlagged(row.flags) ? 'unstar' : 'star'}
                      keyName="s"
                      label={isFlagged(row.flags) ? 'Remove star' : 'Star'}
                      glyph={isFlagged(row.flags) ? '★' : '☆'}
                      onAct={onAct}
                    />
                    <RowAction
                      row={row}
                      id={isUnread(row.flags) ? 'read' : 'unread'}
                      keyName={isUnread(row.flags) ? 'Shift+I' : 'Shift+U'}
                      label={isUnread(row.flags) ? 'Mark read' : 'Mark unread'}
                      glyph={isUnread(row.flags) ? '◉' : '◌'}
                      onAct={onAct}
                    />
                  </span>
                ) : null}
              </span>
            </div>
          )
        })}
      </div>
      <div className="list-foot">
        {walk.loading ? (
          <span className="spinner">Loading…</span>
        ) : walk.cursor ? (
          <>
            <span className="list-count">
              {walk.rows.length.toLocaleString()} shown
              {walk.count?.exact ? ` of ${walk.count.value.toLocaleString()}` : ''}
            </span>
            <button type="button" className="list-more" onClick={onMore}>
              Load {pageSize.toLocaleString()} more
            </button>
          </>
        ) : (
          // `nextCursor === null` IS THE ONLY END-OF-WALK SIGNAL. Saying "that
          // is all of them" on a short page instead would be a lie the reader
          // acts on.
          <span className="list-count">
            {walk.rows.length.toLocaleString()} {walk.rows.length === 1 ? 'message' : 'messages'} — that is all of them
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Who a row is ABOUT.
 *
 * IN SENT IT IS NOT THE SENDER, and the list used to render
 * `senderName(from_addr_name)` unconditionally — so `in:Sent` was a column of
 * the account holder's own name, repeated down the page, with the one fact that
 * distinguishes those rows (who each went to) already on the wire in `to_addrs`
 * and drawn nowhere.
 *
 * `senderName` NAMES AN ADDRESS HEADER, whichever header it came from — the
 * RFC 2047 decode and the name-then-address fallback are the same job for a
 * recipient as for a sender, and a second copy of them here is exactly the
 * duplication this repo keeps paying for. Only the count is new, and it comes
 * from core's parser rather than from counting commas, because a display name
 * may contain one.
 */
function correspondent(row: MessageRow, outgoing: ReadonlySet<string>): string {
  if (!outgoing.has(row.mailbox)) return senderName(row.from_addr_name)
  if (!row.to_addrs) return '(no recipient)'
  const others = parseAddressList(row.to_addrs).length - 1
  const name = senderName(row.to_addrs)
  return others > 0 ? `${name} +${others}` : name
}

/**
 * One button in a row's action cluster.
 *
 * THE KEY IS IN THE NAME, always. The pointer surface exists because the
 * keyboard one was the only one; making each button say which key does the same
 * thing is what stops the two being separate vocabularies for the same client.
 * The glyph is decorative and `aria-hidden` — the accessible name is the label.
 */
function RowAction({
  row,
  id,
  keyName,
  label,
  glyph,
  onAct,
}: {
  row: MessageRow
  id: string
  keyName: string
  label: string
  glyph: string
  onAct: (id: string, row: MessageRow) => void
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="row-action"
      title={`${label} (${keyName})`}
      aria-label={`${label} (${keyName})`}
      onClick={(event) => {
        // The cluster is inside `.row`, whose own click sets the cursor and
        // opens the message. Archiving something must not also open it.
        event.stopPropagation()
        onAct(id, row)
      }}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  )
}

/**
 * The reading pane.
 *
 * TEXT ONLY, AND DELIBERATELY SO. `/messages/:guid/body` returns text; `hasHtml`
 * says a rich half exists. No sanitiser ships until there is a sandboxed frame
 * to render into, because that frame — no scripts, no remote loads — is the
 * security boundary, and a sanitiser on its own is a losing arms race against
 * markup a stranger wrote. The notice below is what the reader sees meanwhile,
 * rather than silently showing them a flattened version of a message that had
 * a layout.
 */
/**
 * "Copy link" — the permalink, fetched only when somebody asks for one.
 *
 * NOT COMPUTED IN THE CLIENT, and it could not be: the ref is
 * HMAC-SHA256(site_secret, ...) and the secret lives on the origin. That is
 * deliberate — a client that could mint refs would be a client holding a key
 * that authenticates every permalink in the household.
 *
 * NOT FETCHED ON EVERY MESSAGE EITHER. Most messages are read and never shared,
 * and asking the origin for a ref each time somebody moves the cursor would be a
 * request per keystroke of `j`.
 *
 * The URL handed over is `/m/<ref>` on this origin, which `router.tsx` resolves.
 * It is safe to paste anywhere: a ref is an identifier, not a capability, so it
 * is worth nothing to anybody without a session on this household's mail.
 */
function CopyLink({ guid }: { guid: string }): React.JSX.Element {
  const [state, setState] = useState<'idle' | 'copying' | 'copied' | 'failed'>('idle')

  // Reset when the message changes, so a "Copied" from the last one does not
  // sit there claiming something about this one.
  useEffect(() => setState('idle'), [guid])

  const copy = async (): Promise<void> => {
    setState('copying')
    try {
      const { ref } = await api.ref(guid)
      const url = `${location.origin}/m/${ref}`
      // `clipboard` is absent over plain http and in some embedded webviews.
      // Falling back to showing the URL is better than a button that fails
      // silently — the reader can still select it.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
        setState('copied')
      } else {
        window.prompt('Copy this link', url)
        setState('idle')
      }
    } catch {
      setState('failed')
    }
  }

  return (
    <button type="button" className="copy-link" onClick={() => void copy()} disabled={state === 'copying'}>
      {state === 'copied' ? 'Link copied' : state === 'failed' ? 'No link available' : 'Copy link'}
    </button>
  )
}

/** One button in the reading pane's toolbar. See `RowAction` for the naming rule. */
function ReadAction({
  id,
  keyName,
  label,
  onClick,
}: {
  id: string
  keyName: string
  label: string
  onClick: () => void
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="read-action"
      data-action={id}
      title={`${label} (${keyName})`}
      // THE KEY IS PART OF THE NAME, not only of the tooltip. It teaches the
      // keyboard surface, and it is also what keeps "Archive the message" and
      // "Archive the mailbox" in the nav from being the same control to a
      // screen reader — and to a test.
      aria-label={`${label} (${keyName})`}
      onClick={onClick}
    >
      {label}
    </button>
  )
}


/**
 * The reading pane: one message's furniture, around a conversation.
 *
 * WHAT IS LEFT HERE IS THE HEADER. Everything below it moved to `Thread.tsx`,
 * because the pane's job turned out to be two jobs: the subject, the toolbar and
 * the permalink are about the ROW THE LIST IS ON, and the body is about A
 * MESSAGE — and in a conversation those are not the same thing. The toolbar
 * still acts on the row, which is the newest message of the thread and the one
 * every keyboard binding already targets; each message's own body, images and
 * mark-read timer belong to the message.
 */
function ReadingPane({
  row,
  prefs,
  onMarkRead,
  onBack,
  onAct,
  onCommand,
  canWrite,
  canSend,
  focusSignal,
  onFocusedMessage,
}: {
  row: MessageRow | null
  prefs: ReadingPrefs
  onMarkRead: (row: MessageRow) => void
  /** Back to the list. Only reachable under 60rem, where the pane covers it. */
  onBack: () => void
  /** Triage THIS message, never the selection. See `act`. */
  onAct: (id: string, row: MessageRow) => void
  /** Reply, reply-all and forward already act on the cursor row. */
  onCommand: (id: string) => boolean | void
  canWrite: boolean
  canSend: boolean
  /** Bumped by Enter, so the conversation can take focus. See `ConversationProps`. */
  focusSignal: number
  /** Which message in the conversation the reader is on. See `ConversationProps`. */
  onFocusedMessage: (row: MessageRow | null) => void
}): React.JSX.Element {
  if (!row) {
    return (
      <div className="state">
        <span className="state-title">No message selected</span>
        <span>Pick one from the list, or search for it.</span>
      </div>
    )
  }

  /**
   * IS THIS ROW A CONVERSATION — asked of `Thread.tsx`, never re-derived here.
   *
   * The pane and the conversation must agree about it exactly: the header that
   * says "4 messages" and the walk that fetches them are the same decision, and
   * two spellings of `thread_count > 1` in two files is precisely the bug this
   * repo keeps paying for.
   */
  const thread = conversationKey(row) !== null
  /**
   * Is this one on the reply-later list?
   *
   * THROUGH `flagSet`, the same parser `isUnread` and `isFlagged` use, because
   * the wire's `flags` is one space-delimited string and a `includes()` test
   * would match `$replylatertomorrow` and any keyword that contains this one.
   */
  const forLater = flagSet(row.flags).has(REPLY_LATER_KEYWORD)

  return (
    <>
      <header className="read-head">
        {/* A BUTTON AND NOT ONLY A KEY. `u` goes back, and a phone has no `u`
            to press — the stylesheet hides this above 60rem, where the list is
            beside the message and there is nothing to go back from. */}
        <button type="button" className="read-back" onClick={onBack}>
          ← All messages
        </button>
        <h1 className="read-subject">{row.subject || '(no subject)'}</h1>
        {/* WHOSE MESSAGE THIS IS — of a conversation, nobody's in particular.
            The sender line names the row's own sender, and a `group=thread` row
            is only the NEWEST message: printing "Mr Patel · Tuesday" over four
            messages from three people states something false about three of
            them. Each message names itself in its own header instead. */}
        {thread ? (
          <div className="read-meta">{many(row.thread_count ?? 0, 'message')} in this conversation</div>
        ) : (
          <>
            <div className="read-meta">
              {senderName(row.from_addr_name)} · {fullDate(row.date)} · {row.mailbox}
            </div>
            {/* WHO IT WENT TO, which the client has never shown anywhere — so there
                was no way to check who a Reply all was about to reach before
                pressing it. `to_addrs` has always been on the wire.
                THROUGH THE SAME DECODER AS THE FROM LINE above, because
                `to_addrs` is a raw header: a recipient with a non-ASCII name is
                stored RFC 2047-encoded, and printing it raw put `=?utf-8?B?…?=` on
                the one line added so a reader could check who they were about to
                reply to. Two treatments of one thing in adjacent lines. */}
            {row.to_addrs ? <div className="read-meta read-to">To: {recipientLine(row.to_addrs)}</div> : null}
          </>
        )}
        {/* THE TOUCH SURFACE, and the only one there is. The row cluster needs a
            hover to appear, and a phone has none — so under 60rem, where a
            tapped row IS an opened message, this toolbar is the whole pointer
            path. Every button names its key so the two vocabularies stay one. */}
        <div className="read-actions">
          {canWrite ? (
            <>
              <ReadAction id="archive" keyName="e" label="Archive" onClick={() => onAct('archive', row)} />
              <ReadAction id="trash" keyName="#" label="Delete" onClick={() => onAct('trash', row)} />
              <ReadAction id="spam" keyName="!" label="Report spam" onClick={() => onAct('spam', row)} />
              <ReadAction
                id="star"
                keyName="s"
                label={isFlagged(row.flags) ? 'Remove star' : 'Star'}
                onClick={() => onAct(isFlagged(row.flags) ? 'unstar' : 'star', row)}
              />
              <ReadAction
                id="read"
                keyName={isUnread(row.flags) ? 'Shift+I' : 'Shift+U'}
                label={isUnread(row.flags) ? 'Mark read' : 'Mark unread'}
                onClick={() => onAct(isUnread(row.flags) ? 'read' : 'unread', row)}
              />
              {/* THE WAY BACK OFF THE LIST, and the reason `h` can be a plain
                  add. `h` marks a message the way `s` stars one — no toggle at
                  the keyboard, because a selection of twelve rows has no single
                  state to toggle — so the pointer path is where one message's
                  mark comes off again. Without it, `g h` would be a destination
                  nothing could ever leave. */}
              <ReadAction
                id="replyLater"
                keyName="h"
                label={forLater ? 'Clear reply later' : 'Reply later'}
                onClick={() => onAct(forLater ? 'unreplyLater' : 'replyLater', row)}
              />
              {/* THE WAY OUT OF A SNOOZE, AND THE ONLY ONE THERE IS.
                  `b` snoozes until tomorrow morning and there is no key that
                  undoes it: like `s` and `h` beside it, the keyboard verb is a
                  plain act because a selection of twelve rows has no single
                  state to toggle, so the pointer path is where one message's
                  snooze comes off again. `runCommand` has had an `unsnooze`
                  arm since snooze landed and NOTHING CALLED IT — a message
                  parked until Tuesday could only be brought back by waiting for
                  Tuesday.

                  ONLY WHEN THE MESSAGE IS ACTUALLY ASLEEP, read from
                  `snoozed_until` and never from the mailbox: snoozing moves the
                  message into the archive, so "is it in the archive" would
                  offer this on everything the reader ever filed, and pressing
                  it would move that back to the Inbox. */}
              {typeof row.snoozed_until === 'number' && row.snoozed_until > 0 ? (
                <ReadAction
                  id="unsnooze"
                  keyName="b"
                  label="Bring back now"
                  onClick={() => onAct('unsnooze', row)}
                />
              ) : null}
            </>
          ) : null}
          {canSend ? (
            <>
              <ReadAction id="reply" keyName="r" label="Reply" onClick={() => onCommand('reply')} />
              <ReadAction id="replyAll" keyName="a" label="Reply all" onClick={() => onCommand('replyAll')} />
              <ReadAction id="forward" keyName="f" label="Forward" onClick={() => onCommand('forward')} />
            </>
          ) : null}
        </div>
        <CopyLink guid={row.id} />
      </header>

      <Conversation
        row={row}
        prefs={prefs}
        onMarkRead={onMarkRead}
        focusSignal={focusSignal}
        onFocusedMessage={onFocusedMessage}
      />
    </>
  )
}
